import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, apiClient } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { useTranslation } from 'react-i18next';
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CornerLabelField } from "@/components/ui/corner-label-field";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Calendar, Check } from "lucide-react";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { insertTicketSchema, SYSTEM_USER_ID, type InsertTicket, type Project, type Team, type Ticket, type Gewerk, type Typ, type Status, type Plan, type TeamGroup } from "@mangelapp/shared";
import { format } from "date-fns";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { RefreshCw, X, Upload, Edit3, ChevronRight, ChevronDown, ChevronLeft, Maximize2, Sparkles } from "lucide-react";
import { LogoLoader } from "@/components/ui/logo-loader";
import { useWeatherStore } from "@/stores/weatherStore";
import { usePermissions } from "@/hooks/use-permissions";
import { useCurrentOrganization } from "@/hooks/use-current-organization";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useProjectCurrency } from "@/hooks/use-project-currency";
import { TicketImageUploader } from "@/components/TicketImageUploader";
import { useDateFormat } from '@/utils/date-formatter';
import ImageEditor from "@/components/ImageEditor";
import TicketHistory from "@/components/ticket-history";
import ImageAnalysisDialog from "@/components/image-analysis-dialog";
import AnalysisReviewDialog from "@/components/analysis-review-dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import type { TicketImageMetadata } from "@/types/api";
import { getInitials } from "@/lib/utils";
import { cn } from "@/lib/utils";
// PDF.js import für PDF-Rendering
import * as pdfjsLib from 'pdfjs-dist';
import type { ExportOptions } from '@/pages/project-details/services/TicketPDFExportService';
import { TicketPDFExportServiceTicketsTab, type TicketExportDataTicketsTab } from '@/pages/project-details/services/TicketPDFExportServiceTicketsTab';
import { PlanImageCaptureService } from '@/pages/project-details/services/PlanImageCaptureService';
import { TicketExportOptionsModal } from '@/pages/project-details/components/TicketExportOptionsModal';
import webIconSendSvg from '@/assets/web_icon_send.svg';
import iconLogoFolwOutline from '@/assets/icon_logo_folw_outline.svg';
import iconBookmarkSvg from '@/assets/icon_bookmark.svg';
import iconBookmarkFilledSvg from '@/assets/icon_bookmark_filled.svg';
import iconHeartOutlineSvg from '@/assets/icon_heart_outline.svg';
import iconHeartFilledSvg from '@/assets/icon_heart_filled.svg';
import webShareIconSvg from '@/assets/web_share_icon.svg';
import { QUERY_KEYS } from "@/pages/project-details/utils/project-constants";
import { TicketCreatedAtField } from "@/components/TicketCreatedAtField";
import { ticketDueDateToInputValue as toDateInputValue } from '@/utils/ticket-calendar-date';

// PDF.js Worker konfigurieren
pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

// Extend HTMLCanvasElement to include our custom property
declare global {
  interface HTMLCanvasElement {
    _pdfRenderTask?: any;
  }
}

const createFormSchema = (t: (key: string) => string) => z.object({
  projectId: z.string().min(1, "Project is required"),
  title: z.string().min(1, t('tickets:form.titleRequired')),
  description: z.string().optional(),
  // Status and priority are always required - if original values were deleted, user must select new ones
  // Preprocess undefined to empty string so validation works correctly
  statusId: z.preprocess(
    (val) => val === undefined || val === null ? "" : val,
    z.string().min(1, t('tickets:form.statusRequired') || "Please select a status")
  ),
  priorityId: z.preprocess(
    (val) => val === undefined || val === null ? "" : val,
    z.string().min(1, t('tickets:form.priorityRequired') || "Please select a priority")
  ),
  gewerkId: z.string().nullable().optional(),
  typeId: z.string().nullable().optional(),
  location: z.string().optional(),
  assigneeEmail: z.string().optional(),
  dueDate: z.string().optional(),
  extensionDate: z.string().optional(),
  progress: z.number().min(0).max(100),
  reporterName: z.string().optional(),
  assignedContractorId: z.string().optional(),
  assignedGroupId: z.string().optional(),
  costs: z.string().optional(),
  positionId: z.string().optional(),
  overtimeNeeded: z.boolean().optional().default(false),
  overtimeHours: z.number().min(1).max(24).multipleOf(0.5).nullable().optional(),
  overtimeStartDate: z.string().optional(),
  overtimeDeliveryDate: z.string().optional(),
}).refine((data) => {
  // If overtimeNeeded is true, overtimeHours must be provided
  if (data.overtimeNeeded === true) {
    return data.overtimeHours !== null && data.overtimeHours !== undefined;
  }
  return true;
}, {
  message: "Overtime hours are required when overtime is needed",
  path: ["overtimeHours"],
}).refine((data) => {
  if (!data.overtimeStartDate || !data.overtimeDeliveryDate) return true;
  return new Date(data.overtimeStartDate).getTime() <= new Date(data.overtimeDeliveryDate).getTime();
}, {
  message: t('components.ticket:overtime.dateOrderError'),
  path: ["overtimeDeliveryDate"],
});

type FormData = z.infer<ReturnType<typeof createFormSchema>>;

const overtimeHourOptions = Array.from({ length: 47 }, (_, index) =>
  Number((1 + index * 0.5).toFixed(1))
);

// Chat Message Interface
interface ChatMessage {
  id: string;
  ticketId: string;
  userId: string;
  message: string;
  createdAt: string;
  userName: string;
  userCode: string;
}

interface NewTicketInlineFormProps {
  projectId: string;
  onSuccess: (ticketData?: any) => void;
  onCancel: (hasChanges?: boolean) => void;
  onValidationError?: () => void; // Callback when validation fails (e.g., to reset saving state)
  editTicket?: Ticket | null;
  planLocation?: {x: number, y: number, planId: string, zoom: number};
  onFormStateChange?: (isDirty: boolean) => void;
  hidePlanPreview?: boolean;
  onSubmitRef?: (submitFn: () => void) => void;
  projectName?: string; // Optional: project name to avoid fetching all projects when editing
  prefetchedPlans?: Plan[];
  prefetchedPriorities?: Array<{ id: string; name: string; displayName?: string; color?: string }>;
  prefetchedStatus?: Status[];
  prefetchedGewerke?: Gewerk[];
  prefetchedTypen?: Typ[];
  prefetchedTeamGroups?: TeamGroup[];
  onActionsReady?: (actions: {
    canExportPDF: boolean;
    isExporting: boolean;
    isPending: boolean;
    canSave: boolean;
    hasChanges: boolean;
    onExportPDF: () => void;
    onSave: () => Promise<boolean>;
    onCancel: () => void;
  }) => void;
}

export default function NewTicketInlineForm({
  projectId,
  onSuccess,
  onCancel,
  onValidationError,
  editTicket,
  planLocation,
  onFormStateChange,
  hidePlanPreview = false,
  onSubmitRef,
  projectName,
  prefetchedPlans,
  prefetchedPriorities,
  prefetchedStatus,
  prefetchedGewerke,
  prefetchedTypen,
  prefetchedTeamGroups,
  onActionsReady
}: NewTicketInlineFormProps) {
  const { dateFormat, timeZone, formatDate, formatDateTime, formatTime } = useDateFormat();
  
  const { t: tChat } = useTranslation(['components.ticketChat']);
  const { t, i18n } = useTranslation(['components.ticket', 'common', 'components.voiceNote', 'tickets']);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const ticketTempIdMapQueryKey = useMemo(
    () => ['/api/tickets/temp-id-map', projectId] as const,
    [projectId],
  );
  const tempTicketSyncErrorKey = 'components.ticket:editModal.errors.ticketSyncInProgress';
  
  // Create form schema with translations
  const formSchema = useMemo(() => createFormSchema(t), [t]);
  
  // Get organization ID for queries
  const { organizationId, organizationName } = useCurrentOrganization();
  
  // Check permissions using the new organization-based system
  const { 
    hasPermission, 
    canConfirmOvertime, 
    canEditTickets, 
    canAddPhotosToTickets,
    canMessageInTickets,
    canCreateTickets 
  } = usePermissions({ organizationId: organizationId || undefined });
  const canEditTicketFields = canEditTickets();
  const canAddPhotosInTickets = canAddPhotosToTickets();
  const canSendMessagesInTickets = canMessageInTickets();
  const canUseAI = hasPermission('ai_image_analysis');
  
  // Get current user for optimistic updates
  const { user } = useCurrentUser();
  const userId = (user as any)?.id || null;
  
  // Get project currency for cost display
  const currency = useProjectCurrency(projectId);
  
  // Field restriction logic based on permissions
  const shouldRestrictField = !canEditTicketFields;
  // In edit mode, users without edit permission are fully read-only.
  const isReadOnlyEditTicket = Boolean(editTicket) && !canEditTicketFields;
  const shouldRestrictFieldForLimitedEdit = isReadOnlyEditTicket;
  const canOnlyAddPhotosInEditMode =
    Boolean(editTicket) && !canEditTicketFields && canAddPhotosInTickets;
  const canUploadPhotosInCurrentContext = !editTicket || canAddPhotosInTickets;
  const canMessageInCurrentContext = !editTicket || canSendMessagesInTickets;

  const resolvePersistedTicketId = useCallback(
    async (ticketId: string): Promise<string | null> => {
      if (!ticketId) return null;
      if (!ticketId.startsWith('temp-')) return ticketId;

      const readMappedId = () => {
        const idMap = queryClient.getQueryData<Record<string, string>>(ticketTempIdMapQueryKey);
        const mapped = idMap?.[ticketId];
        if (mapped && !mapped.startsWith('temp-')) {
          return mapped;
        }

        const projectTicketsData = queryClient.getQueryData<any>(['/api/tickets', 'project', projectId]);
        const projectTickets = Array.isArray(projectTicketsData?.data)
          ? projectTicketsData.data
          : [];
        const cacheMatch = projectTickets.find((ticket: any) =>
          (ticket?.__clientTempId === ticketId || ticket?.id === ticketId) &&
          typeof ticket?.id === 'string' &&
          !ticket.id.startsWith('temp-'),
        );
        return cacheMatch?.id ?? null;
      };

      const immediateMatch = readMappedId();
      if (immediateMatch) {
        return immediateMatch;
      }

      const startedAt = Date.now();
      while (Date.now() - startedAt < 8000) {
        await new Promise((resolve) => setTimeout(resolve, 200));
        const resolvedId = readMappedId();
        if (resolvedId) {
          return resolvedId;
        }
      }

      return null;
    },
    [projectId, queryClient, ticketTempIdMapQueryKey],
  );
  

  const canvasRef = useRef<HTMLCanvasElement>(null);
  /** Frozen when opening create flow; sent as `createdAt` on POST (stable ticket document instant). */
  const newTicketDocumentCapturedAtUtcRef = useRef<string | null>(null);
  if (!editTicket && newTicketDocumentCapturedAtUtcRef.current === null) {
    const n = new Date();
    newTicketDocumentCapturedAtUtcRef.current = new Date(
      Date.UTC(
        n.getFullYear(),
        n.getMonth(),
        n.getDate(),
        n.getHours(),
        n.getMinutes(),
        n.getSeconds(),
      ),
    ).toISOString();
  }
  const lastSubmitTime = useRef<number>(0);
  const [planImageLoaded, setPlanImageLoaded] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  // Only fetch weather for new tickets, not when editing (use saved weather instead)
  const { getProjectWeather, projectWeatherLoading, forceRefreshWeather, fetchProjectWeather } = useWeatherStore();
  
  // Get current weather data for this project (only needed for new ticket creation)
  const projectWeather = useMemo(() => {
    // Don't fetch weather when editing - use saved weather from ticket instead
    if (editTicket) return null;
    return getProjectWeather(projectId);
  }, [getProjectWeather, projectId, editTicket]);
  const weatherData = projectWeather?.weather || null;

  // Fetch weather data on mount for new tickets
  useEffect(() => {
    if (!editTicket && projectId) {
      // Delay to ensure project is fully saved before fetching weather
      // Longer delay for newly created projects to ensure they're available in DB
      const timeoutId = setTimeout(() => {
        // Check if we need to fetch weather (not in store, no weather data, or stale)
        const existingWeather = getProjectWeather(projectId);
        const needsFetch = !existingWeather || 
                          !existingWeather.weather || 
                          (existingWeather.lastUpdated && new Date(existingWeather.lastUpdated) < new Date(Date.now() - 60 * 60 * 1000));
        
        if (needsFetch) {
          // fetchProjectWeather now has built-in retry logic
          fetchProjectWeather(projectId).catch(() => {
            // Silently fail - retry logic is handled in fetchProjectWeather
          });
        }
      }, 300); // Increased delay to ensure project is available
      
      return () => clearTimeout(timeoutId);
    }
  }, [editTicket, projectId, getProjectWeather, fetchProjectWeather]);
  const [images, setImages] = useState<string[]>([]);
  const [imagesChanged, setImagesChanged] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const imageUploadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isImageUploadingRef = useRef(false);
  const [editingImageIndex, setEditingImageIndex] = useState<number | null>(null);
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [expandedWeatherImages, setExpandedWeatherImages] = useState<Set<number>>(new Set());
  const [previewImageIndex, setPreviewImageIndex] = useState<number | null>(null);
  const previewSwipeStartXRef = useRef<number | null>(null);
  
  // AI Analysis state
  const [enableAIAnalysis, setEnableAIAnalysis] = useState(false); // Default to false - AI analysis disabled by default
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [pendingAnalysis, setPendingAnalysis] = useState<{
    imageUrl: string;
    analysis: any;
  } | null>(null);
  const [showAnalysisDialog, setShowAnalysisDialog] = useState(false);
  // Store all analyses for review before ticket save
  const [allAnalyses, setAllAnalyses] = useState<Array<{
    imageUrl: string;
    analysis: any;
    approved?: boolean;
    useForTicket?: boolean;
    saveAsImageDesc?: boolean;
    editedDescription?: string;
  }>>([]);
  // Review dialog state
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  // Store pending form submission data
  const pendingSubmissionRef = useRef<FormData | null>(null);
  // Local state for image descriptions (updated immediately when user approves)
  const [localImageDescriptions, setLocalImageDescriptions] = useState<Map<string, string>>(new Map());

  // Keyboard navigation for image preview
  useEffect(() => {
    if (previewImageIndex === null || images.length === 0) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setPreviewImageIndex(previewImageIndex > 0 ? previewImageIndex - 1 : images.length - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setPreviewImageIndex(previewImageIndex < images.length - 1 ? previewImageIndex + 1 : 0);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setPreviewImageIndex(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewImageIndex, images.length]);

  // Debug log for image uploading state changes
  useEffect(() => {
    // console.log('🖼️ Image uploading state changed:', isImageUploading);
    isImageUploadingRef.current = isImageUploading;
  }, [isImageUploading]);
  
  // State für individuelles User-Like-Status
  const [userHasLiked, setUserHasLiked] = useState<boolean>(false);
  const [isLikeOperationInProgress, setIsLikeOperationInProgress] = useState<boolean>(false);
  // Local state for like count to persist even when editTicket prop changes
  const [localLikeCount, setLocalLikeCount] = useState<number | undefined>(undefined);
  const [isBookmarkOperationInProgress, setIsBookmarkOperationInProgress] = useState<boolean>(false);
  
  // Chat State
  const [newMessage, setNewMessage] = useState<string>("");
  const [isSendingChatMessage, setIsSendingChatMessage] = useState<boolean>(false);
  const chatScrollContainerRef = useRef<HTMLDivElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const hasScrolledChatRef = useRef(false);
  const lastChatMessageCountRef = useRef(0);
  
  // Query für User-Like-Status
  const { data: userLikeStatus } = useQuery({
    queryKey: [`/api/tickets/${editTicket?.id}/user-liked`],
    queryFn: async () => {
      const response = await apiRequest(`/api/tickets/${editTicket?.id}/user-liked`);
      return await response.json();
    },
    enabled: !!editTicket?.id,
    staleTime: 30000,
  });

  const { data: bookmarksResponse } = useQuery({
    queryKey: ['/api/saved-photos', organizationId],
    queryFn: async () => {
      const url = organizationId
        ? `/api/saved-photos?organizationId=${encodeURIComponent(organizationId)}`
        : '/api/saved-photos';
      const response = await apiRequest(url);
      return await response.json();
    },
    enabled: !!editTicket?.id,
  });

  const bookmarkData = useMemo(() => {
    if (!editTicket?.id || !bookmarksResponse?.success || !Array.isArray(bookmarksResponse.data)) {
      return null;
    }
    return bookmarksResponse.data.find((bookmark: any) =>
      bookmark.metadata?.type === 'ticket_bookmark' &&
      bookmark.metadata?.ticketId === editTicket.id &&
      Number(bookmark.metadata?.imageIndex ?? 0) === 0
    );
  }, [bookmarksResponse, editTicket?.id]);

  const isBookmarked = !!bookmarkData;
  
  // Chat-Nachrichten laden
  const { data: messagesResponse, isLoading: isChatLoading } = useQuery<{
    messages: ChatMessage[];
    unreadCount: number;
  }>({
    queryKey: ["/api/tickets", editTicket?.id, "chat"],
    queryFn: async () => apiClient.getTicketChat(editTicket!.id),
    enabled: !!editTicket?.id,
  });

  // Fetch image metadata including weather data for each image
  const { data: imageMetadataResponse } = useQuery<{ items: TicketImageMetadata[] }>({
    queryKey: [`/api/tickets/${editTicket?.id}/image-metadata`],
    queryFn: async () => {
      const response = await apiClient.getTicketImageMetadata(editTicket!.id);
      return response as { items: TicketImageMetadata[] };
    },
    enabled: !!editTicket?.id && images.length > 0,
  });

  const imageMetadata: TicketImageMetadata[] = imageMetadataResponse?.items || [];
  const currentPreviewImageUrl = previewImageIndex !== null ? images[previewImageIndex] : null;

  const { data: previewImageAnalysisResponse } = useQuery<{
    success: boolean;
    data?: { analysisText?: string | null } | null;
  }>({
    queryKey: ["/api/ai/image-analysis", editTicket?.id, currentPreviewImageUrl],
    queryFn: async () => {
      if (!editTicket?.id || !currentPreviewImageUrl) {
        return { success: false, data: null };
      }
      return apiClient.getImageAnalysis(editTicket.id, currentPreviewImageUrl) as Promise<{
        success: boolean;
        data?: { analysisText?: string | null } | null;
      }>;
    },
    enabled: !!editTicket?.id && !!currentPreviewImageUrl,
    staleTime: 60_000,
  });
  
  // Create a map of imageUrl -> metadata for quick lookup
  // Merge with local descriptions (for immediate updates before query refetch)
  const metadataMap = new Map<string, TicketImageMetadata>();
  imageMetadata.forEach((meta) => {
    // Check if we have a local description override for this image
    const localDesc = localImageDescriptions.get(meta.imageUrl);
    if (localDesc !== undefined) {
      // Update the metadata with local description
      metadataMap.set(meta.imageUrl, { ...meta, description: localDesc });
    } else {
      metadataMap.set(meta.imageUrl, meta);
    }
  });
  
  // Also add local descriptions for images that don't have metadata yet (new tickets or just uploaded)
  localImageDescriptions.forEach((description, imageUrl) => {
    if (!metadataMap.has(imageUrl) && images.includes(imageUrl)) {
      // Create a temporary metadata entry for display
      metadataMap.set(imageUrl, {
        id: `temp-${imageUrl}`,
        ticketId: editTicket?.id || '',
        imageUrl,
        imageIndex: images.indexOf(imageUrl),
        description,
        addedBy: '',
        addedAt: new Date(),
      } as TicketImageMetadata);
    }
  });

  // Toggle weather expansion for a specific image
  const toggleWeatherExpansion = (index: number) => {
    setExpandedWeatherImages((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const messages = messagesResponse?.messages || [];

  useEffect(() => {
    hasScrolledChatRef.current = false;
    lastChatMessageCountRef.current = 0;
  }, [editTicket?.id]);

  useEffect(() => {
    if (!editTicket?.id || isChatLoading) return;
    const container = chatScrollContainerRef.current;
    if (!container) return;

    const isFirstScroll = !hasScrolledChatRef.current;
    const hasNewMessage = messages.length !== lastChatMessageCountRef.current;
    if (!isFirstScroll && !hasNewMessage) return;

    requestAnimationFrame(() => {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: isFirstScroll ? "auto" : "smooth",
      });
    });

    hasScrolledChatRef.current = true;
    lastChatMessageCountRef.current = messages.length;
  }, [editTicket?.id, isChatLoading, messages.length]);

  // Nachricht senden
  const sendMessageMutation = useMutation({
    mutationFn: async (message: string) => {
      setIsSendingChatMessage(true);
      return apiRequest(`/api/tickets/${editTicket?.id}/chat`, {
        method: "POST",
        body: JSON.stringify({ message }),
        headers: { "Content-Type": "application/json" },
      });
    },
    onSuccess: () => {
      // Cache aktualisieren
      queryClient.invalidateQueries({
        queryKey: ["/api/tickets", editTicket?.id, "chat"],
      });
      setNewMessage("");
      // Chat-Flag nach kurzer Verzögerung zurücksetzen
      setTimeout(() => setIsSendingChatMessage(false), 1000);
    },
    onError: (error) => {
      console.error("Chat-Fehler:", error);
      setIsSendingChatMessage(false);
      toast({
        title: tChat('components.ticketChat:errorTitle'),
        description: tChat('components.ticketChat:errorSend'),
        variant: "destructive",
      });
    },
  });
  
  // Sync local like count with editTicket prop ONLY when ticket ID changes
  // This prevents background refetches from overwriting our server-confirmed updates
  useEffect(() => {
    if (editTicket) {
      // Only sync when ticket ID changes, not when likes change
      // This ensures we get the initial value but don't get overwritten by stale refetches
      setLocalLikeCount(editTicket.likes);
    }
  }, [editTicket?.id]); // Only depend on ticket ID, not likes

  useEffect(() => {
    // Don't overwrite state if we're in the middle of a like operation
    if (isLikeOperationInProgress) return;
    
    if ((userLikeStatus as any)?.data?.hasLiked !== undefined) {
      setUserHasLiked((userLikeStatus as any).data.hasLiked);
    } else if ((userLikeStatus as any)?.hasLiked !== undefined) {
      // Fallback for old format
      setUserHasLiked((userLikeStatus as any).hasLiked);
    }
  }, [userLikeStatus, isLikeOperationInProgress]);

  // Chat-Handler
  const handleSendMessage = () => {
    if (!canMessageInCurrentContext) {
      return;
    }

    if (newMessage.trim() && !sendMessageMutation.isPending && editTicket?.id) {
      sendMessageMutation.mutate(newMessage.trim());
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTimeShort = (dateString: string) => {
    return formatTime(dateString);
  };

  // Like-Handler für die Inline-Form
  const handleTicketLike = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Verhindert das Schließen des Dialogs
    e.preventDefault(); // Verhindert Standard-Verhalten
    
    if (!editTicket || isLikeOperationInProgress) return; // Nur bei bestehenden Tickets und nicht während Operation

    // Mark operation as in progress (shows loading indicator)
    setIsLikeOperationInProgress(true);

    // Store previous state for error handling
    const previousLikedState = userHasLiked;

    try {
      const endpoint = previousLikedState
        ? `/api/tickets/${editTicket.id}/unlike`
        : `/api/tickets/${editTicket.id}/like`;
      
      const response = await apiRequest(endpoint, {
        method: 'POST'
      });
      const result = await response.json();
      
      if (result.success && result.data) {
        const updatedTicket = result.data;
        
        // Update local state ONLY after successful server response
        setUserHasLiked(!previousLikedState);
        // Always update like count from server response (default to 0 if not provided)
        const newLikeCount = updatedTicket.likes !== null && updatedTicket.likes !== undefined 
          ? updatedTicket.likes 
          : 0;
        setLocalLikeCount(newLikeCount);
        
        // Update query cache directly with server response to prevent stale refetches
        queryClient.setQueryData([`/api/tickets/${editTicket.id}/user-liked`], {
          success: true,
          data: { hasLiked: !previousLikedState }
        });
        
        // Update the project tickets cache if it exists
        queryClient.setQueriesData(
          { queryKey: ["/api/tickets/project", editTicket.projectId] },
          (oldData: any) => {
            if (!oldData) return oldData;
            // Update the ticket in the list if it exists
            if (Array.isArray(oldData)) {
              return oldData.map((t: any) => 
                t.id === editTicket.id 
                  ? { ...t, likes: updatedTicket.likes }
                  : t
              );
            }
            if (oldData?.data && Array.isArray(oldData.data)) {
              return {
                ...oldData,
                data: oldData.data.map((t: any) => 
                  t.id === editTicket.id 
                    ? { ...t, likes: updatedTicket.likes }
                    : t
                )
              };
            }
            return oldData;
          }
        );
        
        // Update all tickets cache (for dashboard, etc.)
        queryClient.setQueriesData(
          { queryKey: ["/api/tickets"] },
          (oldData: any) => {
            if (!oldData) return oldData;
            if (Array.isArray(oldData)) {
              return oldData.map((t: any) => 
                t.id === editTicket.id 
                  ? { ...t, likes: updatedTicket.likes }
                  : t
              );
            }
            if (oldData?.data && Array.isArray(oldData.data)) {
              return {
                ...oldData,
                data: oldData.data.map((t: any) => 
                  t.id === editTicket.id 
                    ? { ...t, likes: updatedTicket.likes }
                    : t
                )
              };
            }
            return oldData;
          }
        );
        
        // Update individual ticket cache if it exists
        queryClient.setQueryData([`/api/tickets/${editTicket.id}`], updatedTicket);
        
        // No need to invalidate queries - we've already updated all relevant caches directly above
        // This eliminates unnecessary background refetches that cause slow database queries
      } else {
        throw new Error(result.message || 'Failed to update like');
      }
      
    } catch (error: any) {
      toast({
        title: t('components.ticket:errors.likeError') || "Fehler",
        description: error.message || t('components.ticket:errors.likeError') || "Like konnte nicht aktualisiert werden.",
        variant: "destructive",
      });
    } finally {
      // Always mark operation as complete
      setIsLikeOperationInProgress(false);
    }
  };

  const handleTicketBookmark = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (!editTicket || isBookmarkOperationInProgress) return;

    setIsBookmarkOperationInProgress(true);

    const wasBookmarked = isBookmarked;
    const previousBookmarkData = bookmarkData;
    const queryKey = ['/api/saved-photos', organizationId];
    const previousQueryData = queryClient.getQueryData(queryKey);
    const primaryImageUrl = images[0] || '';

    queryClient.setQueryData(queryKey, (oldData: any) => {
      if (!oldData?.data) return oldData;

      if (wasBookmarked && previousBookmarkData) {
        return {
          ...oldData,
          data: oldData.data.filter((b: any) => b.id !== previousBookmarkData.id),
        };
      }

      const tempBookmark = {
        id: `temp-${editTicket.id}-0`,
        metadata: {
          type: 'ticket_bookmark',
          ticketId: editTicket.id,
          imageIndex: 0,
        },
      };

      return {
        ...oldData,
        data: [...oldData.data, tempBookmark],
      };
    });

    try {
      if (wasBookmarked && previousBookmarkData) {
        const response = await apiRequest(`/api/saved-photos/${previousBookmarkData.id}`, {
          method: 'DELETE',
        });
        const result = await response.json();

        if (!result.success) {
          queryClient.setQueryData(queryKey, previousQueryData);
        } else {
          queryClient.invalidateQueries({ queryKey: ['/api/saved-photos'] });
        }
      } else {
        const response = await apiRequest('/api/saved-photos', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            imageUrl: primaryImageUrl,
            organizationId,
            metadata: {
              type: 'ticket_bookmark',
              ticketId: editTicket.id,
              imageIndex: 0,
            },
          }),
        });
        const result = await response.json();

        if (!result.success) {
          queryClient.setQueryData(queryKey, previousQueryData);
        } else {
          queryClient.invalidateQueries({ queryKey: ['/api/saved-photos'] });
        }
      }
    } catch (error) {
      queryClient.setQueryData(queryKey, previousQueryData);
      console.error('Bookmark operation failed:', error);
    } finally {
      setIsBookmarkOperationInProgress(false);
    }
  };
  
  // Only fetch all projects when creating a new ticket AND projectName is not provided
  // When editing a ticket on project details page, we already have project data
  const { data: projectsResponse } = useQuery({
    queryKey: ["/api/projects"],
    queryFn: async () => {
      const response = await apiRequest("/api/projects");
      return await response.json();
    },
    enabled: !editTicket && !projectName, // Only fetch when creating new ticket without project name
    staleTime: 5 * 60 * 1000, // 5 minutes cache
    gcTime: 10 * 60 * 1000, // 10 minutes garbage collection
  });

  const projects = projectsResponse?.data || [];
  
  // Use provided projectName or find from projects array, or fallback
  const effectiveProjectName = projectName || 
    (projects.find((p: any) => p.id === projectId)?.name) || 
    'Project';

  // Normalize various stored priority values to this form's select values
  const normalizePriority = useCallback((value?: string | null): 'low' | 'normal' | 'high' | 'critical' | '' => {
    if (!value) return '';
    const v = String(value).toLowerCase().trim();
    if (['low', 'niedrig', 'düşük'].includes(v)) return 'low';
    if (['medium', 'mittel', 'normal', 'orta'].includes(v)) return 'normal';
    if (['high', 'hoch', 'yüksek'].includes(v)) return 'high';
    if (['critical', 'kritisch', 'kritik'].includes(v)) return 'critical';
    return 'normal';
  }, []);

  // Map raw priority name from DB to a stable code, then to translated label for display
  const priorityNameToCode = useCallback((name?: string | null): 'low' | 'normal' | 'high' | 'critical' | null => {
    if (!name) return null;
    const v = String(name).toLowerCase().trim();
    if (['niedrig', 'low', 'düşük'].includes(v)) return 'low';
    if (['mittel', 'medium', 'normal', 'orta'].includes(v)) return 'normal';
    if (['hoch', 'high', 'yüksek'].includes(v)) return 'high';
    if (['kritisch', 'critical', 'kritik'].includes(v)) return 'critical';
    return null;
  }, []);

  const findPriorityByNameOrDisplayName = useCallback(
    (rawValue: string, availablePriorities: any[]) => {
      const normalizedRaw = String(rawValue).toLowerCase().trim();
      return availablePriorities.find((p: any) =>
        String(p?.name || '').toLowerCase().trim() === normalizedRaw ||
        String(p?.displayName || '').toLowerCase().trim() === normalizedRaw
      );
    },
    []
  );

  const findLowPriority = useCallback((availablePriorities: any[]) => {
    return availablePriorities.find((p: any) => {
      const normalizedName = String(p?.name || '').toLowerCase().trim();
      const normalizedDisplayName = String(p?.displayName || '').toLowerCase().trim();
      return ['niedrig', 'low', 'düşük'].includes(normalizedName) ||
        ['niedrig', 'low', 'düşük'].includes(normalizedDisplayName);
    });
  }, []);

  const resolvePriorityIdForTicket = useCallback(
    (ticket: any, availablePriorities: any[]) => {
      if (!Array.isArray(availablePriorities) || availablePriorities.length === 0) {
        return '';
      }

      const ticketPriorityId = String(ticket?.priorityId || '').trim();
      if (ticketPriorityId) {
        const idMatch = availablePriorities.find(
          (p: any) => String(p?.id || '').toLowerCase() === ticketPriorityId.toLowerCase()
        );
        if (idMatch?.id) {
          return idMatch.id;
        }
      }

      const rawPriority = String(ticket?.priority || ticket?.prioritaet || '').trim();
      if (rawPriority) {
        const directMatch = findPriorityByNameOrDisplayName(rawPriority, availablePriorities);
        if (directMatch?.id) {
          return directMatch.id;
        }

        const code = priorityNameToCode(rawPriority);
        if (code) {
          const codeSet = code === 'low'
            ? ['niedrig', 'low', 'düşük']
            : code === 'normal'
              ? ['mittel', 'medium', 'normal', 'orta']
              : code === 'high'
                ? ['hoch', 'high', 'yüksek']
                : ['kritisch', 'critical', 'kritik'];

          const codeMatch = availablePriorities.find((p: any) => {
            const normalizedName = String(p?.name || '').toLowerCase().trim();
            const normalizedDisplayName = String(p?.displayName || '').toLowerCase().trim();
            return codeSet.includes(normalizedName) || codeSet.includes(normalizedDisplayName);
          });

          if (codeMatch?.id) {
            return codeMatch.id;
          }
        }
      }

      const lowPriority = findLowPriority(availablePriorities);
      if (lowPriority?.id) {
        return lowPriority.id;
      }

      return String(availablePriorities[0]?.id || '');
    },
    [findLowPriority, findPriorityByNameOrDisplayName, priorityNameToCode]
  );

  // Use displayName from API (database-backed translation) for priority
  const getPriorityDisplayName = useCallback((item: any): string => {
    // Accept either full item with displayName or just name string for backward compatibility
    if (typeof item === 'object' && item !== null) {
      return item.displayName || item.name || '-';
    }
    // Legacy: if a string is passed directly, return it
    return item || '-';
  }, []);

  // Use displayName from API (database-backed translation) for status
  const getStatusDisplayName = useCallback((item: any): string => {
    // Accept either full item with displayName or just name string for backward compatibility
    if (typeof item === 'object' && item !== null) {
      return item.displayName || item.name || '-';
    }
    // Legacy: if a string is passed directly, return it
    return item || '-';
  }, []);

  const hasPrefetchedPlans = Array.isArray(prefetchedPlans);
  const hasPrefetchedPriorities = Array.isArray(prefetchedPriorities) && prefetchedPriorities.length > 0;
  const hasPrefetchedStatus = Array.isArray(prefetchedStatus) && prefetchedStatus.length > 0;
  const hasPrefetchedGewerke = Array.isArray(prefetchedGewerke) && prefetchedGewerke.length > 0;
  const hasPrefetchedTypen = Array.isArray(prefetchedTypen) && prefetchedTypen.length > 0;
  const hasPrefetchedTeamGroups = Array.isArray(prefetchedTeamGroups);

  // Plans for export and preview
  const { data: plansResponse } = useQuery({
    queryKey: QUERY_KEYS.projectPlans(projectId),
    queryFn: async () => {
      const response = await apiRequest(`/api/plans/project/${projectId}?limit=200&offset=0`);
      return await response.json();
    },
    enabled: !!projectId && !hasPrefetchedPlans,
  });
  const plans = hasPrefetchedPlans ? (prefetchedPlans || []) : (plansResponse?.data || []);

  // Export PDF for edit mode - show modal first
  const handleExportPDF = () => {
    if (!editTicket) return;
    setIsExportModalOpen(true);
  };

  // Helper function to convert image URL to base64
  const convertImageUrlToBase64 = async (imageUrl: string): Promise<string | null> => {
    try {
      // If already base64, return as is
      if (imageUrl.startsWith('data:image/')) {
        return imageUrl;
      }

      // If blob URL, fetch and convert
      if (imageUrl.startsWith('blob:')) {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      }

      // For regular URLs (GCS, etc.), fetch and convert
      const response = await fetch(imageUrl);
      if (!response.ok) {
        console.warn('Failed to fetch image:', imageUrl);
        return null; // Image not accessible (might be deleted)
      }
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error converting image to base64:', imageUrl, error);
      return null; // Return null for failed conversions (deleted/invalid images)
    }
  };

  // Perform actual export with options
  const performExport = async (options: ExportOptions) => {
    if (!editTicket) return;
    
    setIsExportModalOpen(false); // Close modal first
    setIsExporting(true);
    try {
      let planImageData: string | undefined;
      const planLoc = (editTicket as any).planLocation;
      
      // Use stored preview image if available (preferred - same settings as PDF export)
      if ((editTicket as any).planPreviewImage) {
        planImageData = (editTicket as any).planPreviewImage;
        console.log('Using stored preview image for PDF export:', {
          ticketNumber: (editTicket as any).ticketNumber,
          hasPinPosition: !!(editTicket as any).planPreviewPinPosition
        });
      }
      // Otherwise, generate on-the-fly (backward compatibility for old tickets)
      else if (planLoc?.planId) {
        const plan = Array.isArray(plans) ? plans.find((p: any) => p.id === planLoc.planId) : undefined;
        try {
          const ticketNum = (editTicket as any).ticketNumber || editTicket.id;
          const capture = await PlanImageCaptureService.capturePlanSection(
            plan?.fileUrl || '',
            planLoc,
            2, // zoom level (2x for better detail)
            { width: 400, height: 300 }, // capture size
            ticketNum // render pin with ticket number in image
          );
          planImageData = capture.imageData;
          console.log('Generated plan image on-the-fly for PDF export:', {
            ticketNumber: ticketNum
          });
        } catch (e) {
          console.error('Plan capture failed for export:', e);
        }
      }

      // Fetch additional data based on options
      let photos: string[] | undefined;
      let history: any[] | undefined;
      let messages: any[] | undefined;

      if (options.includePhotos) {
        // Photos are already in editTicket.images array
        // Convert URLs to base64 and filter out invalid/deleted images
        const imageUrls = ((editTicket as any).images || []) as string[];
        const convertedImages = await Promise.all(
          imageUrls.map(url => convertImageUrlToBase64(url))
        );
        // Filter out null values (failed conversions = deleted/invalid images)
        photos = convertedImages.filter((img): img is string => img !== null);
      }

      if (options.includeHistory) {
        try {
          const historyResponse = await apiClient.getTicketHistory(editTicket.id, {
            limit: 200,
            offset: 0,
          });
          history = historyResponse.items;
        } catch (error) {
          console.error('Error fetching ticket history:', error);
          history = [];
        }
      }

      if (options.includeMessages) {
        try {
          const chat = await apiClient.getTicketChat(editTicket.id);
          messages = chat.messages;
        } catch (error) {
          console.error('Error fetching ticket messages:', error);
          messages = [];
        }
      }

      const exportTicket: TicketExportDataTicketsTab = {
        id: editTicket.id,
        ticketNumber: (editTicket as any).ticketNumber || editTicket.id,
        title: editTicket.title,
        status: (editTicket as { status?: string }).status,
        priority: (editTicket as any).prioritaet || editTicket.priority,
        assignedContractorName: (editTicket as any).assignedContractorName,
        assigneeName: (editTicket as any).assigneeName,
        location: (editTicket as any).standort || (editTicket as any).location,
        description: (editTicket as any).beschreibung || editTicket.description,
        gewerk: (editTicket as any).gewerk,
        typ: (editTicket as any).typ || (editTicket as any).type,
        dueDate: (editTicket as any).faelligkeitsdatum || (editTicket as any).dueDate,
        extensionDate: (editTicket as any).extensionDate,
        costs: (editTicket as any).geschaetzteKosten || (editTicket as any).costs,
        progress: (editTicket as any).fortschritt || (editTicket as any).progress,
        planLocation: planLoc,
        planName: (() => { const plan = Array.isArray(plans) ? plans.find((p: any) => p.id === planLoc?.planId) : undefined; return plan?.name; })(),
        planId: planLoc?.planId,
        planImageData,
        photos,
        history,
        messages,
        creatorName: (editTicket as any).creatorName || (editTicket as any).reporterName,
        reporterName: (editTicket as any).reporterName,
        projectName: effectiveProjectName,
        currency: currency || 'EUR',
        updatedAt: (editTicket as any).updatedAt,
        overtimeNeeded: Boolean((editTicket as any).overtimeNeeded),
        overtimeHours: (editTicket as any).overtimeHours ?? undefined,
        overtimeConfirmed: (editTicket as any).overtimeConfirmed ?? undefined,
        weatherTemperature: (editTicket as any).weatherData?.temperature,
        weatherConditions: (editTicket as any).weatherData?.conditions,
        weatherWind: (editTicket as any).weatherData?.wind,
      };

      const exporter = new TicketPDFExportServiceTicketsTab(i18n.language, {
        dateFormat,
        timeZone,
      });
      await exporter.exportTickets([exportTicket], effectiveProjectName, options);
    } catch (error) {
      console.error('PDF Export Fehler (inline form):', error);
    } finally {
      setIsExporting(false);
    }
  };

  const { data: teamMembersResponse } = useQuery({
    queryKey: ["/api/contractors", organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/contractors?organizationId=${encodeURIComponent(organizationId)}` : "/api/contractors";
      const response = await apiRequest(url);
      return await response.json();
    },
    enabled: true,
  });

  const teamMembers = teamMembersResponse?.data || [];
  const teamMemberOptions = (Array.isArray(teamMembers) ? teamMembers : []).map((m: any) => {
    // Prioritize display name, then construct full name from name + lastName, then fall back to email
    const fullName = `${m.name || m.first_name || ''} ${m.lastName || m.last_name || ''}`.trim();
    const displayName = m.displayName || m.display_name || fullName || m.email || m.id;
    
    return {
      // Use id (ticket_assignees.id) to match ticket.assignedContractorId
      // Fall back to user_id for backward compatibility with legacy data
      id: m.id || m.user_id,
      name: displayName,
    };
  });

  // Fetch team groups for assigned group field (project-scoped)
  const { data: teamGroupsResponse } = useQuery<{
    success: boolean;
    data: Array<{
      id: string;
      name: string;
      description?: string;
      color?: string;
    }>;
  }>({
    queryKey: ["/api/team-groups", projectId],
    queryFn: async () => {
      const response = await apiRequest(`/api/team-groups?projectId=${projectId}`);
      return await response.json();
    },
    enabled: !!projectId && !hasPrefetchedTeamGroups,
  });

  const teamGroups = hasPrefetchedTeamGroups ? (prefetchedTeamGroups || []) : (teamGroupsResponse?.data || []);
  const groupOptions = (Array.isArray(teamGroups) ? teamGroups : []).map((g: any) => ({
    id: g.id,
    name: g.name,
  }));

  const { data: gewerkeResponse } = useQuery({
    queryKey: ["/api/gewerke", organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/gewerke?organizationId=${encodeURIComponent(organizationId)}` : "/api/gewerke";
      const response = await apiRequest(url);
      return await response.json();
    },
    enabled: !hasPrefetchedGewerke,
    staleTime: 30 * 60 * 1000, // 30 minutes - gewerke rarely change
    gcTime: 60 * 60 * 1000, // 60 minutes
    refetchOnWindowFocus: false,
  });

  const gewerke = hasPrefetchedGewerke ? (prefetchedGewerke || []) : (gewerkeResponse?.data || []);

  const { data: typenResponse } = useQuery({
    queryKey: ["/api/typen", organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/typen?organizationId=${encodeURIComponent(organizationId)}` : "/api/typen";
      const response = await apiRequest(url);
      return await response.json();
    },
    enabled: !hasPrefetchedTypen,
    staleTime: 30 * 60 * 1000, // 30 minutes - typen rarely change
    gcTime: 60 * 60 * 1000, // 60 minutes
    refetchOnWindowFocus: false,
  });

  const typen = hasPrefetchedTypen ? (prefetchedTypen || []) : (typenResponse?.data || []);

  const { data: statusResponse } = useQuery({
    queryKey: ["/api/status", organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/status?organizationId=${encodeURIComponent(organizationId)}` : "/api/status";
      const response = await apiRequest(url);
      return await response.json();
    },
    enabled: !hasPrefetchedStatus,
    staleTime: 30 * 60 * 1000, // 30 minutes - status rarely changes
    gcTime: 60 * 60 * 1000, // 60 minutes
    refetchOnWindowFocus: false,
  });

  const status = hasPrefetchedStatus ? (prefetchedStatus || []) : (statusResponse?.data || []);

  // Positions
  const { data: positionsResponse } = useQuery({
    queryKey: ["/api/positions", organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/positions?organizationId=${encodeURIComponent(organizationId)}` : "/api/positions";
      const response = await apiRequest(url);
      return await response.json();
    },
    enabled: true,
    staleTime: 30 * 60 * 1000, // 30 minutes - positions rarely change
    gcTime: 60 * 60 * 1000, // 60 minutes
    refetchOnWindowFocus: false,
  });
  const positions = positionsResponse?.data || [];

  // Removed team groups (not needed)

  // Priorities from /listen
  const { data: prioritiesResponse, isLoading: prioritiesLoading } = useQuery({
    queryKey: ["/api/priorities", organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/priorities?organizationId=${encodeURIComponent(organizationId)}` : "/api/priorities";
      const response = await apiRequest(url);
      return await response.json();
    },
    enabled: !hasPrefetchedPriorities,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const priorities = hasPrefetchedPriorities
    ? (prefetchedPriorities || [])
    : (Array.isArray(prioritiesResponse?.data) ? prioritiesResponse.data : []);
  const isPriorityListLoading = !hasPrefetchedPriorities && prioritiesLoading && priorities.length === 0;

  // Populate priorityId after lists load (edit mode) or choose a sane default (create)
  useEffect(() => {
    // Skip if form already has a value
    const current = form.getValues('priorityId');
    if (current) return;

    if (editTicket) {
      const resolvedPriorityId = resolvePriorityIdForTicket(editTicket, priorities);
      if (resolvedPriorityId) {
        form.setValue('priorityId', resolvedPriorityId, { shouldDirty: false, shouldValidate: false });
      }
    } else {
      // Create mode: pick "Low" as default priority
      if (Array.isArray(priorities) && priorities.length > 0) {
        const low = findLowPriority(priorities);
        if (low) {
          form.setValue('priorityId', low.id, { shouldDirty: false, shouldValidate: false });
        } else {
          form.setValue('priorityId', priorities[0].id, { shouldDirty: false, shouldValidate: false });
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priorities, editTicket?.id]);

  // Populate statusId after lists load (edit mode) or choose a sane default (create)
  useEffect(() => {
    // Skip if form already has a value
    const current = form.getValues('statusId');
    if (current) return;

    if (editTicket) {
      // Try to resolve from statusId first (new structure)
      if ((editTicket as any).statusId && Array.isArray(status) && status.length > 0) {
        const match = status.find((s: any) => s.id === (editTicket as any).statusId);
        if (match) {
          form.setValue('statusId', match.id, { shouldDirty: false, shouldValidate: false });
          return;
        }
      }
      
      // Fallback: Try to resolve from legacy string if available
      const raw = (editTicket as any).status || '';
      if (raw && Array.isArray(status) && status.length > 0) {
        // exact match (including new default names)
        let match = status.find((s: any) => String(s.name).toLowerCase() === String(raw).toLowerCase());
        if (match) {
          form.setValue('statusId', match.id, { shouldDirty: false, shouldValidate: false });
          return;
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, editTicket?.id]);

  // Populate gewerkId after lists load (edit mode)
  useEffect(() => {
    // Skip if form already has a value
    const current = form.getValues('gewerkId');
    if (current) return;

    if (editTicket) {
      // Try to resolve from gewerkId first (new structure)
      if ((editTicket as any).gewerkId && Array.isArray(gewerke) && gewerke.length > 0) {
        const match = gewerke.find((g: any) => g.id === (editTicket as any).gewerkId);
        if (match) {
          form.setValue('gewerkId', match.id, { shouldDirty: false, shouldValidate: false });
          return;
        }
      }
      
      // Fallback: Try to resolve from legacy string if available
      const raw = (editTicket as any).gewerk || '';
      if (raw && Array.isArray(gewerke) && gewerke.length > 0) {
        const match = gewerke.find((g: any) => String(g.name).toLowerCase() === String(raw).toLowerCase());
        if (match) {
          form.setValue('gewerkId', match.id, { shouldDirty: false, shouldValidate: false });
          return;
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gewerke, editTicket?.id]);

  // Populate typeId after lists load (edit mode)
  useEffect(() => {
    // Skip if form already has a value
    const current = form.getValues('typeId');
    if (current) return;

    if (editTicket) {
      // Try to resolve from typeId first (new structure)
      if ((editTicket as any).typeId && Array.isArray(typen) && typen.length > 0) {
        const match = typen.find((t: any) => t.id === (editTicket as any).typeId);
        if (match) {
          form.setValue('typeId', match.id, { shouldDirty: false, shouldValidate: false });
          return;
        }
      }
      
      // Fallback: Try to resolve from legacy string if available
      const raw = (editTicket as any).type || (editTicket as any).typ || '';
      if (raw && Array.isArray(typen) && typen.length > 0) {
        const match = typen.find((t: any) => String(t.name).toLowerCase() === String(raw).toLowerCase());
        if (match) {
          form.setValue('typeId', match.id, { shouldDirty: false, shouldValidate: false });
          return;
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typen, editTicket?.id]);

  // Only fetch next ticket number when creating a new ticket (not editing)
  const { data: nextTicketNumberResponse } = useQuery({
    queryKey: ["/api/tickets/next-number", projectId],
    queryFn: async () => {
      const response = await apiRequest(`/api/tickets/next-number/${projectId}`);
      const result = await response.json();
      return result;
    },
    enabled: !!projectId && !editTicket, // Only fetch when creating new ticket, not editing
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  const nextTicketNumber = nextTicketNumberResponse?.data || "01";

  // Align incoming ticket values to current option lists once they load
  useEffect(() => {
    if (!editTicket) return;
    // Normalize gewerk by case-insensitive match to available options
    const currentGewerkId = form.getValues('gewerkId');
    if (currentGewerkId && Array.isArray(gewerke) && gewerke.length > 0) {
      const match = gewerke.find((g: any) => String(g.id).toLowerCase() === String(currentGewerkId).toLowerCase());
      if (match && match.id !== currentGewerkId) {
        form.setValue('gewerkId', match.id, { shouldDirty: false, shouldValidate: false });
      }
    }
    // Normalize type by case-insensitive match to available options
    const currentTypeId = form.getValues('typeId');
    if (currentTypeId && Array.isArray(typen) && typen.length > 0) {
      const match = typen.find((t: any) => String(t.id).toLowerCase() === String(currentTypeId).toLowerCase());
      if (match && match.id !== currentTypeId) {
        form.setValue('typeId', match.id, { shouldDirty: false, shouldValidate: false });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editTicket?.id, gewerke, typen, teamMemberOptions]);


  // Create stable default values
  const defaultValues = {
    projectId: projectId || "",
    title: "",
    description: "",
    priorityId: undefined,
    statusId: undefined,
    location: "",
    reporterName: "",
    assignedContractorId: undefined,
    assignedGroupId: undefined,
    gewerkId: undefined,
    typeId: undefined,
    dueDate: "",
    extensionDate: "",
    costs: "",
    progress: 0,
    positionId: undefined,
    overtimeNeeded: false,
    overtimeHours: undefined,
    overtimeStartDate: "",
    overtimeDeliveryDate: "",
  };

  const form = useForm<FormData>({
    mode: 'onSubmit', // Only validate on submit to prevent initialization errors
    resolver: zodResolver(formSchema),
    defaultValues,
  });
  const {
    formState: { isDirty },
  } = form;
  const overtimeNeededValue = form.watch('overtimeNeeded');
  const overtimeConfirmedValue = Boolean((editTicket as any)?.overtimeConfirmed);


  // Initialize images from editTicket (check both images array and individual fields)
  useEffect(() => {
    if (editTicket) {
      // Prefer images array, fallback to individual fields for migration
      const ticketImages = (editTicket as any).images || [];
      if (ticketImages.length > 0) {
        // Normalize possible object entries to strings
        const normalized = (ticketImages as any[])
          .map((img) => typeof img === 'string' ? img : (img?.imageUrl || img?.url || ''))
          .filter(Boolean);
        setImages(normalized);
      } else {
        // Fallback to individual fields
        const legacyImages: string[] = [];
        if ((editTicket as any).image1Url) legacyImages.push((editTicket as any).image1Url);
        if ((editTicket as any).image2Url) legacyImages.push((editTicket as any).image2Url);
        if ((editTicket as any).image3Url) legacyImages.push((editTicket as any).image3Url);
        if ((editTicket as any).image4Url) legacyImages.push((editTicket as any).image4Url);
        setImages(legacyImages);
      }
    } else {
      // Reset images for new ticket
      setImages([]);
    }
  }, [editTicket?.id]);

  // Reset form only when editTicket id changes (avoid infinite resets)
  useEffect(() => {
    if (editTicket) {
      // console.log('🔄 Resetting form with editTicket data:', editTicket);
      form.reset({
        projectId: editTicket.projectId,
        title: editTicket.title,
        description: editTicket.description,
        priorityId: resolvePriorityIdForTicket(editTicket, priorities),
        statusId: (editTicket as any).statusId || (() => {
          const rawStatus = String((editTicket as { status?: string }).status || '').toLowerCase();
          const match = (status as any[]).find(s =>
            String(s.name).toLowerCase() === rawStatus ||
            String((s as any).displayName || '').toLowerCase() === rawStatus
          );
          return match?.id || "";
        })(),
        reporterName: editTicket.reporterName || "",
        assignedContractorId: (editTicket as any).assignedContractorId || (editTicket as any).assigneeId || undefined,
        assignedGroupId: (editTicket as any).assignedGroupId || undefined,
        location: editTicket.location || "",
        gewerkId: (editTicket as any).gewerkId || (() => {
          const match = (gewerke as any[]).find(g => String(g.name).toLowerCase() === String(editTicket.gewerk || '').toLowerCase());
          return match?.id || undefined;
        })(),
        typeId: (editTicket as any).typeId || (editTicket as any).typId || (() => {
          const rawType = (editTicket as any).type || (editTicket as any).typ || '';
          const match = (typen as any[]).find(t => String(t.name).toLowerCase() === String(rawType).toLowerCase());
          return match?.id || undefined;
        })(),
        // Store as yyyy-MM-dd in form state for stable date input display
        dueDate: toDateInputValue(editTicket.dueDate),
        extensionDate: toDateInputValue(editTicket.extensionDate),
        costs: editTicket.costs || "",
        progress: editTicket.progress || 0,
        positionId: (editTicket as any).positionId || (() => {
          const match = (positions as any[]).find(p => String(p.name).toLowerCase() === String((editTicket as any).position || '').toLowerCase());
          return match?.id || undefined;
        })(),
        overtimeNeeded: (editTicket as any).overtimeNeeded || false,
        overtimeHours: (editTicket as any).overtimeHours || undefined,
        overtimeStartDate: toDateInputValue((editTicket as any).overtimeStartDate),
        overtimeDeliveryDate: toDateInputValue((editTicket as any).overtimeDeliveryDate),
      });
    }
    // Don't reset for new tickets - let the form use its initial default values
  }, [editTicket?.id, form, normalizePriority, gewerke, typen, status, positions]);

  // Set projectId in form when projectId prop changes (for new tickets)
  useEffect(() => {
    if (!editTicket && projectId && projectId !== form.getValues('projectId')) {
      form.setValue('projectId', projectId, { shouldDirty: false, shouldValidate: false });
    }
  }, [projectId, editTicket, form]);

  // Set default status to "OPEN" for new tickets
  useEffect(() => {
    if (!editTicket && status.length > 0 && !form.getValues('statusId')) {
      // Find default open status via language-agnostic semanticType
      const openStatus = (status as any[]).find(
        (s) => String((s as any).semanticType || '').toLowerCase().replace('-', '_') === 'open'
      );
      if (openStatus) {
        form.setValue('statusId', openStatus.id, { shouldDirty: false, shouldValidate: false });
      }
    }
  }, [status, editTicket, form]);

  // Track images changes separately (images are not form fields)
  useEffect(() => {
    if (editTicket) {
      const originalImages = Array.isArray((editTicket as any).images) ? (editTicket as any).images : [];
      const changed = JSON.stringify(images) !== JSON.stringify(originalImages);
      setImagesChanged(changed);
    } else {
      // For new tickets, images are always considered changed if they exist
      setImagesChanged(images.length > 0);
    }
  }, [images, editTicket]);

  // Inform parent component about form changes (combine isDirty with imagesChanged)
  useEffect(() => {
    if (onFormStateChange) {
      onFormStateChange(isDirty || imagesChanged);
    }
  }, [isDirty, imagesChanged, onFormStateChange]);

  // Image upload handler
  const handleImageUpload = async (file: File) => {
    if (!canUploadPhotosInCurrentContext) {
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: t('components.ticket:editModal.toasts.errorTitle') || 'Error',
        description: t('components.ticket:editModal.toasts.invalidImage') || 'Only image files are allowed',
        variant: "destructive",
      });
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: t('components.ticket:editModal.toasts.errorTitle') || 'Error',
        description: 'File is too large. Maximum: 10MB',
        variant: "destructive",
      });
      return;
    }

    // Check max images limit
    if (images.length >= 10) {
      toast({
        title: t('components.ticket:editModal.toasts.errorTitle') || 'Error',
        description: 'Maximum 10 images allowed per ticket',
        variant: "destructive",
      });
      return;
    }

    try {
      setIsImageUploading(true);
      
      // Compress image before upload
      const { compressImage } = await import('@/utils/image-compression');
      const compressedFile = await compressImage(file);
      
      // Upload to server (GCS) and use returned public URL
      const resp: any = await apiClient.uploadTicketImageToStore(compressedFile);
      const imageUrl = resp?.data?.imageUrl || resp?.imageUrl || '';
      if (!imageUrl) throw new Error('No imageUrl returned');
      setImages([...images, imageUrl]);
      // Images changed state will be updated by useEffect
      
      // Trigger AI analysis after successful upload (for both new and existing tickets) - only if enabled and user has permission
      if (enableAIAnalysis && canUseAI) {
        setIsAnalyzing(true);
        try {
          // For new tickets, use form values; for existing, use ticket data
        const ticketContext = editTicket?.id ? {
          title: editTicket.title,
          description: editTicket.description,
          projectName: projectName,
          projectId: projectId,
        } : {
          title: form.getValues('title'),
          description: form.getValues('description'),
          projectName: projectName,
          projectId: projectId,
        };

        // For existing tickets, use real ticket ID; for new tickets, we'll analyze but not save to DB yet
        const ticketIdForAnalysis = editTicket?.id;
        
        if (ticketIdForAnalysis) {
          // For existing tickets, analyze and save immediately
          const analysisResponse = await apiClient.analyzeTicketImage(
            imageUrl,
            ticketIdForAnalysis,
            ticketContext
          );
          
          if (analysisResponse.success && analysisResponse.data) {
            const analysisData = {
              imageUrl,
              analysis: analysisResponse.data,
              approved: undefined,
              useForTicket: false,
              saveAsImageDesc: true, // Default to true - save as image description by default
              editedDescription: analysisResponse.data.analysisText,
            };

            // Store in allAnalyses array
            setAllAnalyses(prev => [...prev, analysisData]);

            // Show dialog immediately after upload
            setPendingAnalysis({
              imageUrl,
              analysis: analysisResponse.data,
            });
            setShowAnalysisDialog(true);
          }
        } else {
          // For new tickets, analyze in preview mode (won't save to DB until ticket is created)
          try {
            const analysisResponse = await apiClient.analyzeTicketImage(
              imageUrl,
              'preview-' + Date.now(), // Preview ID - backend will analyze but not save
              ticketContext
            );
            
            if (analysisResponse.success && analysisResponse.data) {
              const analysisData = {
                imageUrl,
                analysis: {
                  ...analysisResponse.data,
                  isPreview: true, // Mark as preview - will be saved after ticket creation
                },
                approved: undefined,
                useForTicket: false,
                saveAsImageDesc: true, // Default to true - save as image description by default
                editedDescription: analysisResponse.data.analysisText,
              };

              // Store in allAnalyses array (will be saved after ticket creation)
              setAllAnalyses(prev => [...prev, analysisData]);

              // Show dialog immediately after upload
              setPendingAnalysis({
                imageUrl,
                analysis: analysisResponse.data,
              });
              setShowAnalysisDialog(true);
            }
          } catch (error) {
            console.error('Analysis failed for new ticket:', error);
            // Don't show error to user - analysis is optional
          }
        }
      } catch (analysisError) {
        console.error('❌ AI analysis failed:', analysisError);
        // Don't show error to user - analysis is optional
      } finally {
        setIsAnalyzing(false);
      }
    }
    } catch (e) {
      console.error('Image upload failed:', e);
      toast({
        title: t('components.ticket:editModal.toasts.errorTitle') || 'Error',
        description: 'Failed to upload image',
        variant: 'destructive',
      });
    } finally {
      setIsImageUploading(false);
    }
  };

  // Image remove handler
  const handleImageRemove = async (index: number) => {
    if (!canUploadPhotosInCurrentContext) {
      return;
    }

    const imageToRemove = images[index];
    // Clean up object URL if it's one
    if (imageToRemove && imageToRemove.startsWith('blob:')) {
      URL.revokeObjectURL(imageToRemove);
    }
    // Remove from local state; persistence happens on submit
    const updatedImages = images.filter((_, i) => i !== index);
    setImages(updatedImages);
    // Images changed state will be updated by useEffect
  };

  // Image edit handler
  const handleImageEdit = (index: number) => {
    if (!canUploadPhotosInCurrentContext) {
      return;
    }

    setEditingImageIndex(index);
    setShowImageEditor(true);
  };

  // Image editor save handler
  const handleImageEditorSave = async (blob: Blob) => {
    if (!canUploadPhotosInCurrentContext) return;
    if (editingImageIndex === null) return;

    try {
      // Upload edited blob and replace with returned URL
      const resp: any = await apiClient.uploadTicketImageToStore(blob, `edited-${Date.now()}.jpg`);
      const imageUrl = resp?.data?.imageUrl || resp?.imageUrl || '';
      if (!imageUrl) throw new Error('No imageUrl returned');
      const updatedImages = [...images];
      updatedImages[editingImageIndex] = imageUrl;
      setImages(updatedImages);
      // Images changed state will be updated by useEffect
      
      // Close the editor
      setShowImageEditor(false);
      setEditingImageIndex(null);
    } catch (error) {
      console.error('Error saving edited image:', error);
      toast({
        title: t('components.ticket:editModal.toasts.errorTitle') || 'Error',
        description: 'Failed to save edited image',
        variant: "destructive",
      });
    }
  };

  // Image editor close handler
  const handleImageEditorClose = () => {
    setShowImageEditor(false);
    setEditingImageIndex(null);
  };

  const runAIAnalysisForImage = useCallback(async (imageUrl: string) => {
    if (!canUseAI || !imageUrl) return;

    setIsAnalyzing(true);
    try {
      const ticketContext = editTicket?.id ? {
        title: editTicket.title,
        description: editTicket.description,
        projectName: projectName,
        projectId: projectId,
      } : {
        title: form.getValues('title'),
        description: form.getValues('description'),
        projectName: projectName,
        projectId: projectId,
      };

      const ticketIdForAnalysis = editTicket?.id || `preview-${Date.now()}`;
      const analysisResponse = await apiClient.analyzeTicketImage(
        imageUrl,
        ticketIdForAnalysis,
        ticketContext,
      );

      if (!analysisResponse.success || !analysisResponse.data) {
        return;
      }

      const normalizedAnalysis = editTicket?.id
        ? analysisResponse.data
        : {
            ...analysisResponse.data,
            isPreview: true,
          };

      setAllAnalyses((prev) => {
        const filtered = prev.filter((item) => item.imageUrl !== imageUrl);
        return [
          ...filtered,
          {
            imageUrl,
            analysis: normalizedAnalysis,
            approved: undefined,
            useForTicket: false,
            saveAsImageDesc: true,
            editedDescription: analysisResponse.data.analysisText,
          },
        ];
      });

      setPendingAnalysis({
        imageUrl,
        analysis: normalizedAnalysis,
      });
      setShowAnalysisDialog(true);
    } catch (analysisError) {
      console.error('❌ Manual AI analysis failed:', analysisError);
    } finally {
      setIsAnalyzing(false);
    }
  }, [
    canUseAI,
    editTicket?.id,
    editTicket?.title,
    editTicket?.description,
    form,
    projectId,
    projectName,
  ]);

  const persistAnalysesForTicket = useCallback(async (ticketId: string) => {
    if (!ticketId || allAnalyses.length === 0) return;

    const ticketContext = {
      title: form.getValues('title') || editTicket?.title,
      description: form.getValues('description') || editTicket?.description,
      projectName,
      projectId,
    };

    for (const analysisItem of allAnalyses) {
      try {
        if (!analysisItem.imageUrl) continue;
        if (analysisItem.approved === undefined) continue;

        let analysisId = analysisItem.analysis?.id;
        let descriptionToSave = analysisItem.editedDescription || analysisItem.analysis?.analysisText || '';

        // Preview analyses (or missing IDs) must be re-run with the real ticket ID to persist properly
        if (!analysisId || analysisItem.analysis?.isPreview) {
          const analysisResponse = await apiClient.analyzeTicketImage(
            analysisItem.imageUrl,
            ticketId,
            ticketContext
          );

          if (!analysisResponse?.success || !analysisResponse.data?.id) {
            continue;
          }

          analysisId = analysisResponse.data.id;
          descriptionToSave = analysisItem.editedDescription || analysisResponse.data.analysisText || descriptionToSave;
        }

        if (!analysisId) continue;

        await apiClient.saveImageAnalysisApproval(
          analysisId,
          analysisItem.approved,
          descriptionToSave,
          analysisItem.useForTicket || false,
          analysisItem.saveAsImageDesc !== false,
          analysisItem.imageUrl
        );
      } catch (error) {
        console.error('Failed to persist analysis for image:', analysisItem.imageUrl, error);
      }
    }

    queryClient.invalidateQueries({ queryKey: [`/api/tickets/${ticketId}/image-metadata`] });
    setLocalImageDescriptions(new Map());
  }, [allAnalyses, editTicket?.description, editTicket?.title, form, projectId, projectName, queryClient]);

  // 🌤️ NEUES SYSTEM: Wetterdaten laufen bereits im Hintergrund!
  // Keine Verzögerung mehr beim Ticket erstellen - Daten sind sofort verfügbar

  const createTicketMutation = useMutation({
    mutationFn: async (data: InsertTicket) => {
      if (editTicket) {
        const targetTicketId = await resolvePersistedTicketId(editTicket.id);
        if (!targetTicketId) {
          throw new Error(tempTicketSyncErrorKey);
        }

        if (canOnlyAddPhotosInEditMode) {
          const response = await apiClient.updateTicketImages(targetTicketId, (data as any).images || []);
          return { response, targetTicketId };
        }
        const response = await apiClient.patchTicket(targetTicketId, data);
        return { response, targetTicketId };
      } else {
        const response = await apiClient.createTicket(data);
        return { response, targetTicketId: null };
      }
    },
    onMutate: async (data) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['/api/tickets', 'project', projectId] });
      
      // Snapshot the previous value
      const previousTickets = queryClient.getQueryData(['/api/tickets', 'project', projectId]);
      
      // Optimistically update to the new value
      const tempTicketId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const optimisticTicket = editTicket 
        ? { ...editTicket, ...data }
        : (() => {
            const rawCreatedAt = (data as { createdAt?: string | Date }).createdAt;
            const createdAtIso =
              rawCreatedAt != null
                  ? typeof rawCreatedAt === 'string'
                      ? rawCreatedAt
                      : new Date(rawCreatedAt).toISOString()
                  : new Date().toISOString();
            return {
              id: tempTicketId,
              __clientTempId: tempTicketId,
              ...data,
              ticketNumber: '...', // Will be replaced by API response
              createdAt: createdAtIso,
              updatedAt: new Date().toISOString(),
            };
          })();
      
      queryClient.setQueryData(['/api/tickets', 'project', projectId], (old: any) => {
        if (!old?.data) return old;
        
        if (editTicket) {
          // Update existing ticket
          return {
            ...old,
            data: old.data.map((t: any) => 
              t.id === editTicket.id ? { ...t, ...optimisticTicket } : t
            )
          };
        } else {
          // Add new ticket to the beginning
          return {
            ...old,
            data: [optimisticTicket, ...(old.data || [])]
          };
        }
      });
      
      // Return a context object with the snapshotted value
      return { previousTickets, tempTicketId: editTicket ? null : tempTicketId };
    },
    onSuccess: async ({ response, targetTicketId }, _variables, context) => {
      // Extract ticket data from API response: { success: true, data: ticket, message: '...' }
      const ticketData = (response as any)?.data || response;
      const newTicketId = ticketData?.id;
      console.log('✅ Ticket erfolgreich gespeichert:', ticketData?.ticketNumber || newTicketId);
      const persistedTicketId = newTicketId || targetTicketId || editTicket?.id;
      if (persistedTicketId) {
        await persistAnalysesForTicket(persistedTicketId);
      }
      
      // Show success toast
      toast({
        title: editTicket 
          ? t('components.ticket:editModal.success.updateTitle', 'Success')
          : t('components.ticket:editModal.success.createTitle', 'Success'),
        description: editTicket
          ? t('components.ticket:editModal.success.updateDesc', 'The ticket has been updated.')
          : t('components.ticket:editModal.success.createDesc', 'The ticket has been created.'),
        variant: "default",
      });
      
      // Modal already closed optimistically, just notify parent of success
      onSuccess(ticketData);
      
      // Update the optimistic ticket with real data from API
      queryClient.setQueryData(['/api/tickets', 'project', projectId], (old: any) => {
        if (!old?.data) return old;
        
        if (editTicket) {
          // Update the existing ticket with real data
          return {
            ...old,
            data: old.data.map((t: any) => 
              t.id === persistedTicketId ||
              t.id === editTicket.id ||
              t.__clientTempId === editTicket.id
                ? { ...t, ...ticketData, id: persistedTicketId || t.id }
                : t
            )
          };
        } else {
          if (context?.tempTicketId && persistedTicketId) {
            queryClient.setQueryData<Record<string, string>>(ticketTempIdMapQueryKey, (oldMap) => ({
              ...(oldMap || {}),
              [context.tempTicketId]: persistedTicketId,
            }));
          }
          // Replace the optimistic ticket with real data
          return {
            ...old,
            data: old.data.map((t: any) => 
              t.id === context?.tempTicketId || t.__clientTempId === context?.tempTicketId
                ? { ...t, ...ticketData, id: ticketData.id, __clientTempId: context?.tempTicketId }
                : t
            )
          };
        }
      });
      
      // Invalidate next ticket number query after creating a new ticket (not when editing)
      if (!editTicket) {
        queryClient.invalidateQueries({ queryKey: ["/api/tickets/next-number", projectId] });
      }
      
      // 🚀 HISTORIE CACHE nur bei EDIT-Modus invalidieren (MEGA PERFORMANCE)
      if (editTicket && persistedTicketId) {
        queryClient.invalidateQueries({ queryKey: [`/api/tickets/${persistedTicketId}/history`] });
        // Invalidate image metadata query when ticket is updated (images may have been added)
        queryClient.invalidateQueries({ queryKey: [`/api/tickets/${persistedTicketId}/image-metadata`] });
      }
    },
    onError: (error, _data, context) => {
      // Roll back to the previous value on error
      if (context?.previousTickets) {
        queryClient.setQueryData(['/api/tickets', 'project', projectId], context.previousTickets);
      }
      
      console.error("Error creating/updating ticket:", error);
      
      // Detaillierte Fehleranalyse
      if (error instanceof Error) {
        console.error("Fehlerdetails:", error.message);
      }
      
      const errorCode =
        error instanceof ApiError
          ? error.data?.error || error.data?.code
          : (error as any)?.code;
      const backendMessage =
        error instanceof ApiError
          ? (typeof error.data?.message === 'string' && error.data.message.trim().length > 0
              ? error.data.message
              : error.message)
          : (error instanceof Error ? error.message : undefined);
      const translatedBackendMessage = backendMessage && backendMessage.includes('.')
        ? t(backendMessage, backendMessage)
        : backendMessage;
      const isTicketLimitReached = errorCode === 'TICKET_LIMIT_REACHED';
      const isTempSyncError = backendMessage === tempTicketSyncErrorKey;

      toast({
        title: t('components.ticket:editModal.errors.title', 'Error'),
        description: editTicket
          ? (isTempSyncError
              ? t('components.ticket:editModal.errors.ticketSyncInProgress')
              : (translatedBackendMessage || t('components.ticket:editModal.errors.updateFailed', 'The ticket could not be updated.')))
          : (isTicketLimitReached && backendMessage
              ? (translatedBackendMessage || backendMessage)
              : t('components.ticket:editModal.errors.createFailed', 'The ticket could not be created.')),
        variant: "destructive",
      });
    },
  });

  // Mutation for confirming overtime
  const confirmOvertimeMutation = useMutation({
    mutationFn: async () => {
      if (!editTicket?.id) throw new Error('No ticket ID');
      return apiClient.confirmTicketOvertime(editTicket.id);
    },
    onSuccess: (response) => {
      const ticketData = (response as any)?.data || response;
      console.log('✅ Overtime confirmed:', ticketData?.ticketNumber || ticketData?.id);
      
      toast({
        title: t('components.ticket:overtime.confirmedSuccess') || 'Success',
        description: t('components.ticket:overtime.confirmedSuccess') || 'Overtime confirmed successfully',
        variant: "default",
      });
      
      // Optimistically update all ticket caches with the new overtime confirmation status
      // This ensures all users see the update immediately without waiting for refetch
      const overtimeUpdate = {
        overtimeConfirmed: true,
        overtimeConfirmedAt: ticketData.overtimeConfirmedAt || new Date().toISOString(),
        overtimeConfirmedBy: ticketData.overtimeConfirmedBy || userId,
      };
      
      const updateTicketInCache = (oldData: any) => {
        if (!oldData?.data) return oldData;
        if (Array.isArray(oldData.data)) {
          // For array responses (ticket lists)
          return {
            ...oldData,
            data: oldData.data.map((ticket: any) =>
              ticket.id === editTicket?.id
                ? { ...ticket, ...overtimeUpdate }
                : ticket
            )
          };
        }
        return oldData;
      };
      
      // Update all possible ticket query caches optimistically
      if (editTicket?.id) {
        // Update project-specific ticket lists
        if (projectId) {
          queryClient.setQueryData(QUERY_KEYS.projectTickets(projectId), updateTicketInCache);
          queryClient.setQueryData(['/api/tickets', 'project', projectId], updateTicketInCache);
          queryClient.setQueryData(['tickets', 'project', projectId], updateTicketInCache);
        }
        
        // Update dashboard/organization-scoped ticket lists
        if (organizationId) {
          queryClient.setQueryData(["/api/tickets", organizationId], updateTicketInCache);
        }
        // Also update general tickets query (without orgId)
        queryClient.setQueryData(["/api/tickets"], updateTicketInCache);
        queryClient.setQueryData(['tickets'], updateTicketInCache);
        
        // Update individual ticket queries
        queryClient.setQueryData([`/api/tickets/${editTicket.id}`], (old: any) => {
          if (!old?.data) return old;
          return {
            ...old,
            data: {
              ...old.data,
              ...overtimeUpdate
            }
          };
        });
        queryClient.setQueryData(['tickets', editTicket.id], (old: any) => {
          if (!old?.data) return old;
          return {
            ...old,
            data: {
              ...old.data,
              ...overtimeUpdate
            }
          };
        });
      }
      
      // Invalidate all ticket-related queries to ensure all users see the update
      // This includes queries from dashboard, project details, tickets page, etc.
      // Use predicate to match all query keys that contain ticket-related patterns
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          if (!Array.isArray(key) || key.length === 0) return false;
          
          const firstKey = key[0];
          if (typeof firstKey !== 'string') return false;
          
          // Match all ticket query patterns, including those with organizationId
          // Examples: ["/api/tickets"], ["/api/tickets", orgId], ["/api/tickets", "project", projectId], ["tickets"], etc.
          return (
            firstKey === '/api/tickets' ||
            firstKey === 'tickets' ||
            firstKey.startsWith('/api/tickets') ||
            (key.length > 1 && (
              (key[1] === 'project' && (firstKey === '/api/tickets' || firstKey === 'tickets')) ||
              (typeof key[1] === 'string' && firstKey === '/api/tickets') // Matches ["/api/tickets", organizationId]
            ))
          );
        },
        refetchType: 'all' // Refetch all matching queries (both active and inactive) to ensure all users see updates
      });
      
      // Also explicitly invalidate common query key patterns to ensure coverage
      queryClient.invalidateQueries({ queryKey: ["/api/tickets"] });
      if (editTicket?.id) {
        queryClient.invalidateQueries({ queryKey: [`/api/tickets/${editTicket.id}`] });
        queryClient.invalidateQueries({ queryKey: ['tickets', editTicket.id] });
      }
      if (projectId) {
        queryClient.invalidateQueries({ queryKey: ["/api/tickets/project", projectId] });
        queryClient.invalidateQueries({ queryKey: ['/api/tickets', 'project', projectId] });
        queryClient.invalidateQueries({ queryKey: ['tickets', 'project', projectId] });
      }
      // Invalidate with organizationId if available (for dashboard queries)
      if (organizationId) {
        queryClient.invalidateQueries({ queryKey: ["/api/tickets", organizationId] });
      }
      
      // Also invalidate stats queries that might include ticket counts
      queryClient.invalidateQueries({ queryKey: ["/api/stats"] });
      
      // Force refetch all active ticket queries to ensure immediate update for all users
      // This is in addition to invalidation to ensure queries refetch even if they have staleTime
      queryClient.refetchQueries({
        predicate: (query) => {
          const key = query.queryKey;
          if (!Array.isArray(key) || key.length === 0) return false;
          const firstKey = key[0];
          if (typeof firstKey !== 'string') return false;
          return (
            firstKey === '/api/tickets' ||
            firstKey === 'tickets' ||
            firstKey.startsWith('/api/tickets')
          );
        },
        type: 'active' // Only refetch active queries (currently visible on screen)
      });
      
      // Update local editTicket reference if possible
      if (editTicket) {
        (editTicket as any).overtimeConfirmed = true;
        (editTicket as any).overtimeConfirmedAt = ticketData.overtimeConfirmedAt || new Date().toISOString();
        (editTicket as any).overtimeConfirmedBy = ticketData.overtimeConfirmedBy || userId;
      }
    },
    onError: (error) => {
      console.error("Error confirming overtime:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to confirm overtime",
        variant: "destructive",
      });
    },
  });


  const onSubmit = async (data: FormData) => {
    // Verhindere Doppel-Submits mit zeitbasierter Sperre
    const now = Date.now();
    if (createTicketMutation.isPending || (lastSubmitTime.current && now - lastSubmitTime.current < 1000)) {
      return;
    }

    // Verhindere Submit während Bild-Upload
    if (isImageUploading) {
      return;
    }

    lastSubmitTime.current = now;
    
    // Fetch weather data for new tickets (captured at creation time)
    let weatherDataForTicket: any = null;
    if (!editTicket && projectId) {
      try {
        // Fetch fresh weather data for the project
        const weatherResponse = await apiRequest('/api/weather', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ projectId }),
        });
        
        if (weatherResponse.ok) {
          const weatherResult = await weatherResponse.json();
          if (weatherResult.success && weatherResult.data) {
            weatherDataForTicket = {
              temperature: weatherResult.data.temperature,
              wind: weatherResult.data.wind,
              conditions: weatherResult.data.conditions,
              precipitation: weatherResult.data.precipitation,
              location: weatherResult.data.location,
              timestamp: weatherResult.data.timestamp || new Date(),
            };
          }
        }
      } catch (error) {
        console.error('Failed to fetch weather data for ticket creation:', error);
        // Continue without weather data - ticket creation should not fail
      }
    }
    
    // Konvertiere "none" zu null für Gruppen-Zuordnung
    const processedData = {
      ...data,
      dueDate: data.dueDate || null,
      extensionDate: data.extensionDate || null,
    };
    
    // Convert any blob: URLs to base64 just-in-time
    const toDataUrl = (blob: Blob) => new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onloadend = () => resolve(String(r.result || ''));
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
    const imagesForSubmit = await Promise.all((images || []).map(async (img) => {
      if (typeof img === 'string' && img.startsWith('blob:')) {
        const res = await fetch(img);
        const blob = await res.blob();
        return await toDataUrl(blob);
      }
      return img as string;
    }));

    // Resolve names from IDs for compatibility
    const statusObj = (status as any[]).find(s => s.id === processedData.statusId);
    const gewerkObj = (gewerke as any[]).find(g => g.id === processedData.gewerkId);
    const typeObj = (typen as any[]).find(t => t.id === processedData.typeId);
    const positionObj = (positions as any[]).find(p => p.id === processedData.positionId);

    const priorityObj = (priorities as any[]).find(p => p.id === processedData.priorityId);
    
    // Generate preview screenshot if planLocation is present
    // Use zoomLevel: 2 for better detail, pin is rendered directly in the image
    const finalPlanLocation = planLocation || editTicket?.planLocation || null;
    let planPreviewImage: string | null = null;
    
    if (finalPlanLocation) {
      try {
        const plan = plans.find(p => p.id === finalPlanLocation.planId);
        if (plan?.fileUrl) {
          // Use nextTicketNumber for new tickets, or existing ticketNumber for edits
          const ticketNum = editTicket?.ticketNumber || nextTicketNumber || 'N';
          
          // Get status color for the pin
          // For new tickets, use selected statusId; for edits, use ticket's statusId
          const currentStatusId = form.getValues('statusId') || (editTicket as any)?.statusId;
          const statusItem = (status as any[]).find((s: any) => s.id === currentStatusId);
          const pinColor = statusItem?.color || '#ef4444'; // Default to red for open status
          
          const captureResult = await PlanImageCaptureService.capturePlanSection(
            plan.fileUrl,
            finalPlanLocation,
            2, // zoom level (2x for better detail)
            { width: 400, height: 300 }, // capture size
            ticketNum, // render pin with ticket number in image
            pinColor // pin color based on status
          );
          planPreviewImage = captureResult.imageData;
        }
      } catch (error) {
        console.error('Error generating plan preview image:', error);
        // Continue without preview image - ticket creation should not fail
      }
    }
    
    const transformedData = {
      ...processedData,
      // Sichere Standardwerte für alle erforderlichen Felder
      projectId: data.projectId || projectId || "",
      title: data.title,
      description: data.description?.trim() || undefined,
      priorityId: processedData.priorityId ?? null,
      priority: priorityObj?.name || null,
      statusId: processedData.statusId ?? null,
      status: statusObj?.name || null,
      reporterName: data.reporterName || "",
      assignedContractorId: data.assignedContractorId || null,
      assignedGroupId: data.assignedGroupId || null,
      location: data.location || "",
      gewerkId: processedData.gewerkId || null,
      gewerk: gewerkObj?.name || null,
      typeId: processedData.typeId || null,
      type: typeObj?.name || null,
      positionId: processedData.positionId || null,
      position: positionObj?.name || null,
      costs: data.costs || null,
      progress: data.progress || 0,
      planLocation: finalPlanLocation,
      planPreviewImage: planPreviewImage,
      images: imagesForSubmit,
      // Overtime fields
      overtimeNeeded: data.overtimeNeeded || false,
      overtimeHours: data.overtimeNeeded ? (data.overtimeHours || null) : null,
      overtimeStartDate: data.overtimeNeeded && data.overtimeStartDate ? new Date(data.overtimeStartDate) : null,
      overtimeDeliveryDate: data.overtimeNeeded && data.overtimeDeliveryDate ? new Date(data.overtimeDeliveryDate) : null,
      overtimeConfirmed: editTicket ? ((editTicket as any).overtimeConfirmed || false) : false,
      overtimeConfirmedAt: editTicket ? ((editTicket as any).overtimeConfirmedAt || null) : null,
      overtimeRequestedAt: editTicket ? ((editTicket as any).overtimeRequestedAt || null) : null,
      // Include weather data if available (only for new tickets)
      ...(weatherDataForTicket && {
        weatherTemperature: weatherDataForTicket.temperature,
        weatherWind: weatherDataForTicket.wind,
        weatherConditions: weatherDataForTicket.conditions,
        weatherPrecipitation: weatherDataForTicket.precipitation,
        weatherLocation: weatherDataForTicket.location,
        weatherTimestamp: weatherDataForTicket.timestamp,
      }),
      ...(!editTicket && newTicketDocumentCapturedAtUtcRef.current != null
        ? { createdAt: newTicketDocumentCapturedAtUtcRef.current }
        : {}),
    };
    
    createTicketMutation.mutate(transformedData as unknown as InsertTicket);
  };

  // Keep latest onSubmit in a ref to avoid re-creating the exposed submit fn on every render
  const latestOnSubmitRef = useRef(onSubmit);
  useEffect(() => {
    latestOnSubmitRef.current = onSubmit;
  }, [onSubmit]);

  // Safe-validate and submit using zod.safeParse to avoid resolver throwing
  // Returns true if validation passed (modal should close), false otherwise
  const submitWithValidation = useCallback(async () => {
    if (isReadOnlyEditTicket && !canOnlyAddPhotosInEditMode) {
      onValidationError?.();
      return false;
    }

    const values = form.getValues();
    const result = formSchema.safeParse(values);
    if (!result.success) {
      // Clear previous errors and set new ones
      form.clearErrors();
      for (const issue of result.error.issues) {
        const field = issue.path?.[0] as any;
        if (field) {
          form.setError(field, { type: 'validation', message: issue.message }, { shouldFocus: false });
        }
      }
      // Notify parent that validation failed (e.g., to reset saving state)
      onValidationError?.();
      return false;
    }

    // If this is a new ticket and there are unresolved analyses, show review dialog
    const hasUnresolvedAnalyses = !editTicket && allAnalyses.some((analysis) => analysis.approved === undefined);
    if (hasUnresolvedAnalyses) {
      // Store form data for submission after review
      pendingSubmissionRef.current = result.data as FormData;
      // Show review dialog instead of submitting immediately
      setShowReviewDialog(true);
      return false;
    }

    // For existing tickets or no analyses, submit immediately (fire and forget)
    void latestOnSubmitRef.current(result.data as FormData);
    return true;
  }, [form, formSchema, onValidationError, editTicket, allAnalyses, isReadOnlyEditTicket, canOnlyAddPhotosInEditMode]);

  // Create a stable submit function using useCallback (depends only on stable form instance)
  const stableSubmitFunction = useCallback(() => {
    void submitWithValidation();
  }, [form, submitWithValidation]);

  // Expose submitForm method to parent component via callback
  // Debounce provisioning the submit function to the parent to avoid repeated logs/calls
  const provideTimeoutRef = useRef<number | null>(null);
  useEffect(() => {
    if (!onSubmitRef) return;
    if (provideTimeoutRef.current) {
      clearTimeout(provideTimeoutRef.current);
      provideTimeoutRef.current = null;
    }
    provideTimeoutRef.current = window.setTimeout(() => {
      onSubmitRef(stableSubmitFunction);
    }, 80);

    return () => {
      if (provideTimeoutRef.current) {
        clearTimeout(provideTimeoutRef.current);
        provideTimeoutRef.current = null;
      }
    };
  }, [onSubmitRef, stableSubmitFunction]);

  // Notify parent of action button state changes
  useEffect(() => {
    if (onActionsReady) {
      onActionsReady({
        canExportPDF: !!editTicket,
        isExporting,
        isPending: createTicketMutation.isPending,
        canSave: !createTicketMutation.isPending && (
          !editTicket
            ? true
            : canOnlyAddPhotosInEditMode
              ? imagesChanged
              : !isReadOnlyEditTicket && (isDirty || imagesChanged)
        ),
        hasChanges: isDirty,
        onExportPDF: handleExportPDF,
        onSave: submitWithValidation,
        onCancel: () => onCancel(isDirty),
      });
    }
  }, [editTicket, isExporting, createTicketMutation.isPending, isDirty, imagesChanged, onActionsReady, handleExportPDF, submitWithValidation, onCancel, isReadOnlyEditTicket, canOnlyAddPhotosInEditMode]);

  // Tastatursteuerung für Enter/Return - speichert und schließt Ticket-Formular
  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (isReadOnlyEditTicket) {
      return;
    }

    if (e.key === 'Enter' || e.key === 'Return') {
      const target = e.target as HTMLElement;
      
      // Erlaube Enter in Textareas und Dropdowns
      const isInTextarea = target.matches('textarea');
      const isInCombobox = target.matches('[role="combobox"]') || target.closest('[role="combobox"]');
      const isInModal = target.closest('[role="dialog"]');
      
      if (!isInTextarea && !isInCombobox && !isInModal) {
        e.preventDefault();
        e.stopPropagation();
        void submitWithValidation();
      }
    }
  };

  const creatorName = (editTicket as any)?.creatorName ||
    `${(editTicket as any)?.creatorFirstName || ''} ${(editTicket as any)?.creatorLastName || ''}`.trim() ||
    (editTicket as any)?.reporterName ||
    t('components.ticket:newModal.creator.nameFallback');

  const creatorBio = (editTicket as any)?.creatorBio ||
    (editTicket as any)?.creatorDescription ||
    (editTicket as any)?.creatorCompany ||
    (editTicket as any)?.creatorCity ||
    '';

  const creatorProfilePhotoUrl = (editTicket as any)?.creatorProfilePhotoUrl || null;
  const previewImageMetadata = currentPreviewImageUrl ? metadataMap.get(currentPreviewImageUrl) : null;
  const previewAddedByDisplayName = previewImageMetadata?.addedByDisplayName ||
    (previewImageMetadata?.addedByFirstName && previewImageMetadata?.addedByLastName
      ? `${previewImageMetadata.addedByFirstName} ${previewImageMetadata.addedByLastName}`.trim()
      : previewImageMetadata?.addedByUsername || creatorName);
  const previewAddedByProfilePhotoUrl = previewImageMetadata?.addedByProfilePhotoUrl || creatorProfilePhotoUrl;
  const previewLocation = form.getValues("location") || editTicket?.location || "-";
  const previewOrganizationName = organizationName || (editTicket as any)?.organizationName || "";
  const previewAiDescription = (previewImageAnalysisResponse?.data?.analysisText || "").trim();
  const previewUserDescription = (previewImageMetadata?.description || "").trim();
  const previewDescriptionLines = [
    ...(previewAiDescription ? [previewAiDescription] : []),
    ...(previewUserDescription && previewUserDescription !== previewAiDescription
      ? previewUserDescription.split("\n").map((line) => line.trim()).filter(Boolean)
      : []),
  ];

  const goToPreviousPreviewImage = useCallback(() => {
    if (previewImageIndex === null || images.length <= 1) return;
    setPreviewImageIndex(previewImageIndex > 0 ? previewImageIndex - 1 : images.length - 1);
  }, [previewImageIndex, images.length]);

  const goToNextPreviewImage = useCallback(() => {
    if (previewImageIndex === null || images.length <= 1) return;
    setPreviewImageIndex(previewImageIndex < images.length - 1 ? previewImageIndex + 1 : 0);
  }, [previewImageIndex, images.length]);

  const handlePreviewPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    previewSwipeStartXRef.current = e.clientX;
  }, []);

  const handlePreviewPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (previewSwipeStartXRef.current === null) return;
    const deltaX = e.clientX - previewSwipeStartXRef.current;
    const swipeThreshold = 45;
    if (Math.abs(deltaX) >= swipeThreshold) {
      if (deltaX > 0) {
        goToPreviousPreviewImage();
      } else {
        goToNextPreviewImage();
      }
    }
    previewSwipeStartXRef.current = null;
  }, [goToNextPreviewImage, goToPreviousPreviewImage]);

  return (
    <div className="ticket-modal-body relative">
      {(isImageUploading || isAnalyzing) && (
        <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center z-10 rounded-[16px]">
          <div className="bg-white px-4 py-2 rounded-[8px] shadow-lg flex items-center gap-3">
            <LogoLoader size="sm" />
            <div className="text-sm font-medium text-gray-700">
              {isAnalyzing 
                ? (t('components.ticket:editModal.analyzingImage') || 'Analyzing image with AI...')
                : (t('components.ticket:editModal.imageUploading') || 'Uploading image...')
              }
            </div>
          </div>
        </div>
      )}
      <Form {...form}>
        <form onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); void submitWithValidation(); }} onKeyDown={handleKeyDown} style={{ pointerEvents: isImageUploading ? 'none' : 'auto' }}>
          <div className="ticket-modal-grid">
            {/* Left Column - All Form Fields */}
            <div className="ticket-modal-left-column">
              {/* TICKETS Section */}
              <div className="p-0">
                <div className="space-y-4">
                  {/* Ticket ID und Erstellungsdatum nebeneinander */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Ticket ID */}
                    <CornerLabelField label={t('components.ticket:fields.ticketId')}>
                      <div className="textbox-readonly">
                        <p className="text-dark">#{editTicket ? editTicket.ticketNumber : nextTicketNumber}</p>
                      </div>
                    </CornerLabelField>
                    
                    <TicketCreatedAtField
                      createdAt={
                        editTicket?.createdAt ??
                        newTicketDocumentCapturedAtUtcRef.current ??
                        undefined
                      }
                    />
                  </div>

                  {/* Project Selection - Read Only */}
                  <FormField
                    control={form.control}
                    name="projectId"
                    render={({ field }) => (
                      <FormItem className="space-y-1">
                        <CornerLabelField label={`${t('components.ticket:newModal.project')} *`}>
                          <FormControl>
                            <input type="hidden" {...field} />
                          </FormControl>
                          <div className="textbox-readonly">
                            <p className="text-dark">{effectiveProjectName}</p>
                          </div>
                        </CornerLabelField>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />

                  {/* Title - Read-only for contractors */}
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem className="space-y-1">
                        <CornerLabelField label={`${t('components.ticket:newModal.title')} *`}>
                          <FormControl>
                            {shouldRestrictFieldForLimitedEdit ? (
                              <div className="textbox-readonly">
                                <p className="text-dark">{field.value || '-'}</p>
                              </div>
                            ) : (
                              <Input 
                                placeholder={t('components.ticket:newModal.titlePlaceholder') || 'Enter ticket title...'}
                                {...field} 
                                disabled={shouldRestrictField}
                              />
                            )}
                          </FormControl>
                        </CornerLabelField>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />

                  {/* Description - Read-only for restricted edit users */}
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem className="space-y-1">
                        <CornerLabelField label={t('components.ticket:newModal.description')}>
                          <FormControl>
                            {shouldRestrictFieldForLimitedEdit ? (
                              <div className="textbox-readonly">
                                <p className="text-dark">{field.value || '-'}</p>
                              </div>
                            ) : (
                              <Textarea
                                placeholder={t('components.ticket:newModal.descriptionPlaceholder') || ''}
                                rows={3}
                                className="corner-label-field-textarea-multiline"
                                {...field}
                                disabled={shouldRestrictField}
                              />
                            )}
                          </FormControl>
                        </CornerLabelField>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    {/* Priority (ID-based from /listen) - Read-only for contractors */}
                    <FormField
                      control={form.control}
                      name="priorityId"
                      render={({ field }) => (
                        <FormItem className="space-y-1">
                          <CornerLabelField label={t('components.ticket:newModal.priority')}>
                            <FormControl>
                              {shouldRestrictFieldForLimitedEdit ? (
                                <div className="textbox-readonly">
                                  <p className="text-dark">
                                    {field.value ? getPriorityDisplayName(priorities.find((p: any) => p.id === field.value)) : '-'}
                                  </p>
                                </div>
                              ) : (
                                <Select 
                                  onValueChange={field.onChange}
                                  value={field.value ?? ""}
                                  disabled={shouldRestrictField || isPriorityListLoading}
                                >
                                  <SelectTrigger className={shouldRestrictField ? 'bg-gray-50 text-muted' : ''}>
                                    <SelectValue
                                      placeholder={isPriorityListLoading
                                        ? (t('common:loading') || 'Loading priorities...')
                                        : (t('components.ticket:newModal.selectPriority') || '')}
                                    />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {isPriorityListLoading && (
                                      <SelectItem value="_loading" disabled className="text-xs text-light italic">
                                        {t('common:loading') || 'Loading priorities...'}
                                      </SelectItem>
                                    )}
                                    {priorities.map((p: any) => (
                                      <SelectItem key={p.id} value={p.id} className="text-xs">
                                        <div className="flex items-center gap-2">
                                          {p.color && (
                                            <div 
                                              className="w-3 h-3 rounded-full border border-slate-300 flex-shrink-0"
                                              style={{ backgroundColor: p.color || '#6b7280' }}
                                            />
                                          )}
                                          <span>{getPriorityDisplayName(p)}</span>
                                        </div>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            </FormControl>
                          </CornerLabelField>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />

                    {/* Status (ID-based) - Read-only for contractors */}
                    <FormField
                      control={form.control}
                      name="statusId"
                      render={({ field }) => (
                        <FormItem className="space-y-1">
                          <CornerLabelField label={t('components.ticket:newModal.status')}>
                            <FormControl>
                              {shouldRestrictFieldForLimitedEdit ? (
                                <div className="textbox-readonly">
                                  <div className="flex items-center gap-2 text-dark">
                                    {field.value ? (() => {
                                      const statusItem = status.find((s: any) => s.id === field.value);
                                      if (!statusItem) return '-';
                                      const statusColor = statusItem.color || '#6b7280';
                                      const displayName = getStatusDisplayName(statusItem);
                                      return (
                                        <>
                                          <div 
                                            className="w-2 h-2 rounded-full flex-shrink-0"
                                            style={{ backgroundColor: statusColor }}
                                          />
                                          <span>{displayName}</span>
                                        </>
                                      );
                                    })() : '-'}
                                  </div>
                                </div>
                              ) : (
                                <Select 
                                  onValueChange={(value) => field.onChange(value === "_clear" ? undefined : value)} 
                                  value={field.value ?? ""}
                                  disabled={shouldRestrictField}
                                >
                                  <SelectTrigger className={shouldRestrictField ? 'bg-gray-50 text-muted' : ''}>
                                    <SelectValue placeholder={t('components.ticket:newModal.selectStatus') || ''} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="_clear" className="text-xs text-light italic">
                                      {t('components.ticket:fields.clearSelection')}
                                    </SelectItem>
                                    {status.map((statusItem) => {
                                      const statusColor = statusItem.color || '#6b7280';
                                      const displayName = getStatusDisplayName(statusItem);
                                      return (
                                        <SelectItem 
                                          key={statusItem.id} 
                                          value={statusItem.id} 
                                          className="text-xs"
                                        >
                                          <div className="flex items-center gap-2">
                                            <div 
                                              className="w-2 h-2 rounded-full"
                                              style={{ backgroundColor: statusColor }}
                                            />
                                            <span>{displayName}</span>
                                          </div>
                                        </SelectItem>
                                      );
                                    })}
                                  </SelectContent>
                                </Select>
                              )}
                            </FormControl>
                          </CornerLabelField>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* Gewerk (Trade) - Read-only for contractors */}
                    <FormField
                      control={form.control}
                      name="gewerkId"
                      render={({ field }) => (
                        <FormItem className="space-y-1">
                          <CornerLabelField label={t('components.ticket:fields.trade')}>
                            <FormControl>
                              {shouldRestrictFieldForLimitedEdit ? (
                                <div className="textbox-readonly">
                                  <p className="text-dark">
                                    {field.value ? (gewerke.find((g: any) => g.id === field.value)?.displayName || gewerke.find((g: any) => g.id === field.value)?.name || '-') : '-'}
                                  </p>
                                </div>
                              ) : (
                                <Select
                                  onValueChange={shouldRestrictField ? undefined : (value) => field.onChange(value === "_clear" ? undefined : value)}
                                  value={field.value ?? ""}
                                  disabled={shouldRestrictField}
                                >
                                  <SelectTrigger className={shouldRestrictField ? 'bg-gray-50 text-muted' : ''}>
                                    <SelectValue placeholder={t('components.ticket:fields.trade') || ''} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="_clear" className="text-xs text-light italic">
                                      {t('components.ticket:fields.clearSelection')}
                                    </SelectItem>
                                    {gewerke.map((gewerk) => (
                                      <SelectItem key={gewerk.id} value={gewerk.id} className="text-xs">
                                        {gewerk.displayName || gewerk.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            </FormControl>
                          </CornerLabelField>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />

                    {/* Type - Read-only for contractors */}
                    <FormField
                      control={form.control}
                      name="typeId"
                      render={({ field }) => (
                        <FormItem className="space-y-1">
                          <CornerLabelField label={t('components.ticket:fields.type')}>
                            <FormControl>
                              {shouldRestrictFieldForLimitedEdit ? (
                                <div className="textbox-readonly">
                                  <p className="text-dark">
                                    {field.value ? (typen.find((t: any) => t.id === field.value)?.displayName || typen.find((t: any) => t.id === field.value)?.name || '-') : '-'}
                                  </p>
                                </div>
                              ) : (
                                <Select
                                  onValueChange={shouldRestrictField ? undefined : (value) => field.onChange(value === "_clear" ? undefined : value)}
                                  value={field.value ?? ""}
                                  disabled={shouldRestrictField}
                                >
                                  <SelectTrigger className={shouldRestrictField ? 'bg-gray-50 text-muted' : ''}>
                                    <SelectValue placeholder={t('components.ticket:fields.type') || ''} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="_clear" className="text-xs text-light italic">
                                      {t('components.ticket:fields.clearSelection')}
                                    </SelectItem>
                                    {typen.map((typ) => (
                                      <SelectItem key={typ.id} value={typ.id} className="text-xs">
                                        {typ.displayName || typ.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            </FormControl>
                          </CornerLabelField>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Location - Read-only for contractors */}
                  <FormField
                    control={form.control}
                    name="location"
                    render={({ field }) => (
                      <FormItem className="space-y-1">
                        <CornerLabelField label={t('components.ticket:newModal.location')}>
                          <FormControl>
                            {shouldRestrictFieldForLimitedEdit ? (
                              <div className="textbox-readonly">
                                <p className="text-dark">{field.value || '-'}</p>
                              </div>
                            ) : (
                              <Input 
                                placeholder="z.B. 3. Stock, Südwand" 
                                {...field}
                                value={field.value || ""}
                                disabled={shouldRestrictField}
                              />
                            )}
                          </FormControl>
                        </CornerLabelField>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />

                  {/* Costs - Read-only for contractors */}
                  <FormField
                    control={form.control}
                    name="costs"
                    render={({ field }) => {
                      // Get currency symbol for display
                      const currencySymbols: Record<string, string> = {
                        'EUR': '€',
                        'USD': '$',
                        'GBP': '£',
                        'CHF': 'CHF',
                        'TRY': '₺',
                        'PLN': 'zł',
                        'DKK': 'kr',
                        'SEK': 'kr',
                        'NOK': 'kr',
                        'CZK': 'Kč',
                        'HUF': 'Ft',
                        'RON': 'lei',
                        'BGN': 'лв'
                      };
                      const symbol = currencySymbols[currency] || currency;
                      const costsLabel = `${t('components.ticket:fields.costs')} (${symbol})`;
                      
                      return (
                        <FormItem className="space-y-1">
                          <CornerLabelField label={costsLabel}>
                            <FormControl>
                              {shouldRestrictFieldForLimitedEdit ? (
                                <div className="textbox-readonly">
                                  <p className="text-dark">{field.value ? `${symbol} ${field.value}` : '-'}</p>
                                </div>
                              ) : (
                                <div className="relative">
                                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted pointer-events-none z-10">
                                    {symbol}
                                  </span>
                                  <Input 
                                    placeholder="1.000,00" 
                                    {...field}
                                    value={field.value || ""}
                                    onChange={(e) => {
                                      // Erlaube nur Ziffern, Komma, Punkt und Leerzeichen
                                      const value = e.target.value.replace(/[^\d,.\s]/g, '');
                                      field.onChange(value);
                                    }}
                                    style={{ 
                                      paddingLeft: symbol.length > 2 ? '3.5rem' : '2.5rem',
                                      paddingRight: '0.75rem'
                                    }}
                                    disabled={shouldRestrictField}
                                  />
                                </div>
                              )}
                            </FormControl>
                          </CornerLabelField>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      );
                    }}
                  />

                  {/* Assignee - Read-only for contractors */}
                  <FormField
                    control={form.control}
                    name="assignedContractorId"
                    render={({ field }) => (
                      <FormItem className="space-y-1">
                        <CornerLabelField label={t('components.ticket:newModal.assigneeName')}>
                          <FormControl>
                            {shouldRestrictFieldForLimitedEdit ? (
                              <div className="textbox-readonly">
                                <p className="text-dark">
                                  {field.value ? (teamMemberOptions.find((m: any) => m.id === field.value)?.name || '-') : '-'}
                                </p>
                              </div>
                            ) : (
                              <Select
                                onValueChange={shouldRestrictField ? undefined : (value) => {
                                  field.onChange(value === 'none' ? undefined : value);
                                }}
                                value={field.value ?? ""}
                                disabled={shouldRestrictField}
                              >
                                <SelectTrigger className={shouldRestrictField ? 'bg-gray-50 text-muted' : ''}>
                                  <SelectValue placeholder={t('components.ticket:newModal.assigneePlaceholder') || ''} />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none" className="text-xs text-light">
                                    {t('components.ticket:fields.clearSelection')}
                                  </SelectItem>
                                  {teamMemberOptions.map((member) => (
                                    <SelectItem key={member.id} value={member.id} className="text-xs">
                                      {member.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </FormControl>
                        </CornerLabelField>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />

                  {/* Assigned Group - Read-only for contractors */}
                  <FormField
                    control={form.control}
                    name="assignedGroupId"
                    render={({ field }) => (
                      <FormItem className="space-y-1">
                        <CornerLabelField label={t('components.ticket:fields.assignedGroup')}>
                          <FormControl>
                            {shouldRestrictFieldForLimitedEdit ? (
                              <div className="textbox-readonly">
                                <p className="text-dark">
                                  {field.value ? (groupOptions.find((g: any) => g.id === field.value)?.name || '-') : '-'}
                                </p>
                              </div>
                            ) : (
                              <Select
                                onValueChange={shouldRestrictField ? undefined : (value) => {
                                  field.onChange(value === 'none' ? undefined : value);
                                }}
                                value={field.value ?? ""}
                                disabled={shouldRestrictField}
                              >
                                <SelectTrigger className={shouldRestrictField ? 'bg-gray-50 text-muted' : ''}>
                                  <SelectValue placeholder={t('components.ticket:fields.assignedGroupPlaceholder') || ''} />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none" className="text-xs text-light">
                                    {t('components.ticket:fields.clearSelection')}
                                  </SelectItem>
                                  {groupOptions.map((group) => (
                                    <SelectItem key={group.id} value={group.id} className="text-xs">
                                      {group.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </FormControl>
                        </CornerLabelField>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />

                  {/* Due Date und Extension Date - Read-only for contractors */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Due Date */}
                    <FormField
                      control={form.control}
                      name="dueDate"
                      render={({ field }) => (
                        <FormItem className="space-y-1">
                          <CornerLabelField label={t('components.ticket:fields.dueDate')}>
                            <FormControl>
                              {shouldRestrictFieldForLimitedEdit ? (
                                <div className="textbox-readonly">
                                  <p className="text-dark">{field.value ? formatDate(field.value) : '-'}</p>
                                </div>
                              ) : (
                                <Popover>
                                  <div className="textbox-base flex items-center gap-1">
                                    <PopoverTrigger asChild>
                                      <button
                                        type="button"
                                        disabled={shouldRestrictField}
                                        className="flex w-full items-center justify-end text-right"
                                      >
                                        {field.value ? (
                                          <span className="text-dark">{formatDate(field.value)}</span>
                                        ) : (
                                          <span className="text-light">{t('components.ticket:fields.selectDate')}</span>
                                        )}
                                        <Calendar className="ml-2 h-3 w-3 text-slate-500" />
                                      </button>
                                    </PopoverTrigger>
                                    {field.value ? (
                                      <button
                                        type="button"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          field.onChange('');
                                        }}
                                        className="inline-flex h-4 w-4 shrink-0 items-center justify-center p-0 leading-none text-slate-500 hover:text-slate-700"
                                        aria-label={t('components.ticket:fields.clearSelection')}
                                        title={t('components.ticket:fields.clearSelection')}
                                      >
                                        <X className="h-3 w-3" />
                                      </button>
                                    ) : null}
                                  </div>
                                <PopoverContent className="w-auto p-4" align="start" side="bottom" sideOffset={8} collisionPadding={16}>
                                  <CalendarComponent
                                    mode="single"
                                    selected={field.value ? new Date(field.value) : undefined}
                                    onSelect={(date) => {
                                      field.onChange(date ? format(date, "yyyy-MM-dd") : "");
                                    }}
                                    initialFocus
                                    className="p-4"
                                    classNames={{
                                      months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                                      month: "space-y-4",
                                      table: "w-full border-collapse space-y-2",
                                      head_row: "flex",
                                      head_cell: "text-muted-foreground rounded-md w-12 font-normal !text-base",
                                      row: "flex w-full mt-2",
                                      cell: "h-12 w-12 text-center text-sm p-0 relative",
                                      day: "h-12 w-12 p-0 font-normal aria-selected:opacity-100 !text-lg",
                                    }}
                                  />
                                </PopoverContent>
                              </Popover>
                              )}
                            </FormControl>
                          </CornerLabelField>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />

                    {/* Extension Date */}
                    <FormField
                      control={form.control}
                      name="extensionDate"
                      render={({ field }) => (
                        <FormItem className="space-y-1">
                          <CornerLabelField label={t('components.ticket:fields.extensionDate')}>
                            <FormControl>
                              {shouldRestrictFieldForLimitedEdit ? (
                                <div className="textbox-readonly">
                                  <p className="text-dark">{field.value ? formatDate(field.value) : '-'}</p>
                                </div>
                              ) : (
                                <Popover>
                                  <div className="textbox-base flex items-center gap-1">
                                    <PopoverTrigger asChild>
                                      <button
                                        type="button"
                                        disabled={shouldRestrictField}
                                        className="flex w-full items-center justify-end text-right"
                                      >
                                        {field.value ? (
                                          <span className="text-dark">{formatDate(field.value)}</span>
                                        ) : (
                                          <span className="text-light">{t('components.ticket:fields.selectDate')}</span>
                                        )}
                                        <Calendar className="ml-2 h-3 w-3 text-slate-500" />
                                      </button>
                                    </PopoverTrigger>
                                    {field.value ? (
                                      <button
                                        type="button"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          field.onChange('');
                                        }}
                                        className="inline-flex h-4 w-4 shrink-0 items-center justify-center p-0 leading-none text-slate-500 hover:text-slate-700"
                                        aria-label={t('components.ticket:fields.clearSelection')}
                                        title={t('components.ticket:fields.clearSelection')}
                                      >
                                        <X className="h-3 w-3" />
                                      </button>
                                    ) : null}
                                  </div>
                                <PopoverContent className="w-auto p-4" align="start" side="bottom" sideOffset={8} collisionPadding={16}>
                                  <CalendarComponent
                                    mode="single"
                                    selected={field.value ? new Date(field.value) : undefined}
                                    onSelect={(date) => {
                                      field.onChange(date ? format(date, "yyyy-MM-dd") : "");
                                    }}
                                    initialFocus
                                    className="p-4"
                                    classNames={{
                                      months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                                      month: "space-y-4",
                                      table: "w-full border-collapse space-y-2",
                                      head_row: "flex",
                                      head_cell: "text-muted-foreground rounded-md w-12 font-normal !text-base",
                                      row: "flex w-full mt-2",
                                      cell: "h-12 w-12 text-center text-sm p-0 relative",
                                      day: "h-12 w-12 p-0 font-normal aria-selected:opacity-100 !text-lg",
                                    }}
                                  />
                                </PopoverContent>
                              </Popover>
                              )}
                            </FormControl>
                          </CornerLabelField>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Progress - Read-only for contractors */}
                  <FormField
                    control={form.control}
                    name="progress"
                    render={({ field }) => (
                      <FormItem className="space-y-2">
                        <CornerLabelField label={t('components.ticket:fields.progress')}>
                          <FormControl>
                            {shouldRestrictFieldForLimitedEdit ? (
                              <div className="textbox-readonly">
                                <p className="text-dark">{field.value || 0}%</p>
                              </div>
                            ) : (
                              <div className="space-y-2 px-2">
                                <div className="flex justify-between text-xs text-dark">
                                  <span className="opacity-0">label</span>
                                  <span className="font-medium">{field.value || 0}%</span>
                                </div>
                                <Slider
                                  value={[field.value || 0]}
                                  onValueChange={(value) => {
                                    field.onChange(value[0] ?? 0);
                                  }}
                                  min={0}
                                  max={100}
                                  step={1}
                                  disabled={shouldRestrictField}
                                  className="w-full"
                                  aria-label={t('components.ticket:fields.progress')}
                                />
                              </div>
                            )}
                          </FormControl>
                        </CornerLabelField>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    {/* Overtime Hours - left column */}
                    <FormField
                      control={form.control}
                      name="overtimeHours"
                      render={({ field }) => (
                        <FormItem className="space-y-1">
                          <CornerLabelField label={t('components.ticket:overtime.label')}>
                            <FormControl>
                              {shouldRestrictFieldForLimitedEdit ? (
                                <div className="textbox-readonly">
                                  <p className="text-dark">
                                    {field.value ? `${field.value} ${field.value === 1 ? t('components.ticket:overtime.hour') : t('components.ticket:overtime.hours')}` : '-'}
                                  </p>
                                </div>
                              ) : (
                                <Select
                                  onValueChange={shouldRestrictField ? undefined : (value) => {
                                    field.onChange(value === 'none' ? undefined : parseFloat(value));
                                    if (value === 'none') {
                                      form.setValue('overtimeNeeded', false);
                                      form.setValue('overtimeStartDate', '');
                                      form.setValue('overtimeDeliveryDate', '');
                                    } else {
                                      form.setValue('overtimeNeeded', true);
                                    }
                                  }}
                                  value={field.value ? field.value.toString() : ""}
                                  disabled={shouldRestrictField}
                                >
                                  <SelectTrigger className={shouldRestrictField ? 'bg-gray-50 text-muted' : ''}>
                                    <SelectValue placeholder={t('components.ticket:overtime.selectHours') || ''} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none" className="text-xs text-light italic">
                                      {t('components.ticket:fields.clearSelection')}
                                    </SelectItem>
                                    {overtimeHourOptions.map((hour) => (
                                      <SelectItem key={hour} value={hour.toString()} className="text-xs">
                                        {hour} {hour === 1 ? t('components.ticket:overtime.hour', { defaultValue: 'hour' }) : t('components.ticket:overtime.hours', { defaultValue: 'hours' })}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            </FormControl>
                          </CornerLabelField>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />

                    {/* Employer Approval - right column */}
                    <div className="space-y-1">
                      <CornerLabelField label={t('components.ticket:overtime.employerApproval')}>
                        <div className="textbox-readonly flex items-center justify-between">
                          <p className="text-dark">
                            {overtimeConfirmedValue
                              ? t('components.ticket:overtime.approved')
                              : overtimeNeededValue
                              ? t('components.ticket:overtime.pending')
                              : t('components.ticket:overtime.notSet')}
                          </p>
                          {(overtimeConfirmedValue || overtimeNeededValue) && (
                            <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full ${overtimeConfirmedValue ? 'bg-[#4A90E2]' : 'bg-[#f59e0b]'}`}>
                              <Check className="h-4 w-4 text-white" />
                            </span>
                          )}
                        </div>
                      </CornerLabelField>
                    </div>
                  </div>

                  {overtimeNeededValue && (
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="overtimeStartDate"
                        render={({ field }) => (
                          <FormItem className="space-y-1">
                            <CornerLabelField label={t('components.ticket:overtime.startDate')}>
                              <FormControl>
                                {shouldRestrictFieldForLimitedEdit ? (
                                  <div className="textbox-readonly">
                                    <p className="text-dark">{field.value ? formatDate(field.value) : '-'}</p>
                                  </div>
                                ) : (
                                  <Popover>
                                    <div className="textbox-base flex items-center gap-1">
                                      <PopoverTrigger asChild>
                                        <button
                                          type="button"
                                          disabled={shouldRestrictField}
                                          className="flex w-full items-center justify-end text-right"
                                        >
                                          {field.value ? (
                                            <span className="text-dark">{formatDate(field.value)}</span>
                                          ) : (
                                            <span className="text-light">{t('components.ticket:fields.selectDate')}</span>
                                          )}
                                          <Calendar className="ml-2 h-3 w-3 text-slate-500" />
                                        </button>
                                      </PopoverTrigger>
                                      {field.value ? (
                                        <button
                                          type="button"
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            field.onChange('');
                                          }}
                                          className="inline-flex h-4 w-4 shrink-0 items-center justify-center p-0 leading-none text-slate-500 hover:text-slate-700"
                                          aria-label={t('components.ticket:fields.clearSelection')}
                                          title={t('components.ticket:fields.clearSelection')}
                                        >
                                          <X className="h-3 w-3" />
                                        </button>
                                      ) : null}
                                    </div>
                                    <PopoverContent className="w-auto p-4" align="start" side="bottom" sideOffset={8} collisionPadding={16}>
                                      <CalendarComponent
                                        mode="single"
                                        selected={field.value ? new Date(field.value) : undefined}
                                        onSelect={(date) => {
                                          field.onChange(date ? format(date, "yyyy-MM-dd") : "");
                                        }}
                                        initialFocus
                                        className="p-4"
                                      />
                                    </PopoverContent>
                                  </Popover>
                                )}
                              </FormControl>
                            </CornerLabelField>
                            <FormMessage className="text-xs" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="overtimeDeliveryDate"
                        render={({ field }) => (
                          <FormItem className="space-y-1">
                            <CornerLabelField label={t('components.ticket:overtime.deliveryDate')}>
                              <FormControl>
                                {shouldRestrictFieldForLimitedEdit ? (
                                  <div className="textbox-readonly">
                                    <p className="text-dark">{field.value ? formatDate(field.value) : '-'}</p>
                                  </div>
                                ) : (
                                  <Popover>
                                    <div className="textbox-base flex items-center gap-1">
                                      <PopoverTrigger asChild>
                                        <button
                                          type="button"
                                          disabled={shouldRestrictField}
                                          className="flex w-full items-center justify-end text-right"
                                        >
                                          {field.value ? (
                                            <span className="text-dark">{formatDate(field.value)}</span>
                                          ) : (
                                            <span className="text-light">{t('components.ticket:fields.selectDate')}</span>
                                          )}
                                          <Calendar className="ml-2 h-3 w-3 text-slate-500" />
                                        </button>
                                      </PopoverTrigger>
                                      {field.value ? (
                                        <button
                                          type="button"
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            field.onChange('');
                                          }}
                                          className="inline-flex h-4 w-4 shrink-0 items-center justify-center p-0 leading-none text-slate-500 hover:text-slate-700"
                                          aria-label={t('components.ticket:fields.clearSelection')}
                                          title={t('components.ticket:fields.clearSelection')}
                                        >
                                          <X className="h-3 w-3" />
                                        </button>
                                      ) : null}
                                    </div>
                                    <PopoverContent className="w-auto p-4" align="start" side="bottom" sideOffset={8} collisionPadding={16}>
                                      <CalendarComponent
                                        mode="single"
                                        selected={field.value ? new Date(field.value) : undefined}
                                        onSelect={(date) => {
                                          field.onChange(date ? format(date, "yyyy-MM-dd") : "");
                                        }}
                                        initialFocus
                                        className="p-4"
                                      />
                                    </PopoverContent>
                                  </Popover>
                                )}
                              </FormControl>
                            </CornerLabelField>
                            <FormMessage className="text-xs" />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}

                  {/* Confirm Overtime Button - Only show if overtime is needed, not confirmed, and user has permission */}
                  {editTicket && (editTicket as any).overtimeNeeded && !(editTicket as any).overtimeConfirmed && canConfirmOvertime() && (
                    <div className="flex justify-end mt-2">
                      <Button
                        type="button"
                        variant="default"
                        size="sm"
                        onClick={() => confirmOvertimeMutation.mutate()}
                        disabled={confirmOvertimeMutation.isPending}
                        className="inline-flex items-center gap-1 bg-[#4A90E2] text-white hover:bg-[#3a7bc8]"
                      >
                        {confirmOvertimeMutation.isPending ? (
                          <>
                            <LogoLoader size="sm" />
                            {t('components.ticket:overtime.confirmButton') || 'Confirming...'}
                          </>
                        ) : (
                          t('components.ticket:overtime.confirmButton') || 'Confirm Overtime'
                        )}
                      </Button>
                    </div>
                  )}
                </div>

              </div>
              {/* Images Section */}
              <CornerLabelField
                label={t('components.ticket:newModal.images.title')}
                labelBgClassName="bg-[#d7d7d7]"
              >
                <div className="ticket-modal-section-card">
                  <div className="flex items-center justify-between mb-3">
                    {canUseAI ? (
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="enable-ai-analysis"
                          checked={enableAIAnalysis}
                          onCheckedChange={(checked) => setEnableAIAnalysis(checked === true)}
                          className="corner-checkbox"
                          disabled={!canUploadPhotosInCurrentContext}
                        />
                        <label
                          htmlFor="enable-ai-analysis"
                          className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                        >
                          {t('components.ticket:newModal.images.enableAIAnalysis') || 'Get AI Analysis For Photos'}
                        </label>
                      </div>
                    ) : (
                      <div />
                    )}
                    <div className="text-xs text-muted">
                      {images.length}/10
                    </div>
                  </div>
                  {images.length === 0 ? (
                    <label
                      className={`border-2 border-dashed border-light rounded-[8px] p-8 flex flex-col items-center justify-center min-h-[200px] ${
                        !canUploadPhotosInCurrentContext
                          ? 'cursor-not-allowed opacity-50'
                          : isImageUploading
                            ? 'cursor-wait opacity-50'
                            : 'cursor-pointer hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex flex-col items-center gap-2 mb-2 pointer-events-none">
                        <Upload className="h-8 w-8 text-light" />
                        <div className="text-xs text-muted">{t('components.ticket:newModal.images.uploadImage')}</div>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleImageUpload(file);
                          }
                          e.target.value = '';
                        }}
                        className="hidden"
                        disabled={isImageUploading || !canUploadPhotosInCurrentContext}
                      />
                      {isImageUploading && (
                        <div className="flex flex-col items-center gap-2">
                          <LogoLoader size="sm" />
                          <div className="text-xs text-muted">{t('components.ticket:newModal.images.uploading')}</div>
                        </div>
                      )}
                    </label>
                  ) : (
                    <div className="grid grid-cols-3 gap-px">
                      {images.map((imageUrl, index) => {
                        const imageMetadata = editTicket?.id ? metadataMap.get(imageUrl) : null;
                        const imageDateTime = imageMetadata?.weatherTimestamp
                          ? formatDateTime(imageMetadata.weatherTimestamp)
                          : formatDateTime(new Date());

                        return (
                          <div key={index} className="ticket-modal-image-card">
                            <div className="relative group">
                              <div
                                className="relative aspect-square bg-gray-100 overflow-hidden cursor-pointer"
                                onClick={() => setPreviewImageIndex(index)}
                              >
                                <img
                                  src={imageUrl}
                                  alt={`Image ${index + 1}`}
                                  className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                                  <Maximize2 className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                                <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                  {canUploadPhotosInCurrentContext && (
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleImageEdit(index);
                                      }}
                                      className="h-7 w-7 p-0 bg-primary hover:bg-[#3a7bc8] text-white rounded-full flex items-center justify-center shadow-md"
                                      type="button"
                                      title="Edit image"
                                    >
                                      <Edit3 className="h-4 w-4" />
                                    </button>
                                  )}
                                  {canUploadPhotosInCurrentContext && (
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleImageRemove(index);
                                      }}
                                      className="h-7 w-7 p-0 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-md"
                                      type="button"
                                      title="Delete image"
                                    >
                                      <X className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>
                                <div className="absolute bottom-2 left-2 bg-black/50 text-white px-2 py-1 rounded text-xs">
                                  {imageDateTime}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {canUploadPhotosInCurrentContext && images.length < 10 && (
                        <label className={`aspect-square border-2 border-dashed border-light rounded-[8px] flex flex-col items-center justify-center transition-colors ${
                          isImageUploading ? 'cursor-wait opacity-50' : 'cursor-pointer hover:bg-gray-50'
                        }`}>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                handleImageUpload(file);
                              }
                              e.target.value = '';
                            }}
                            className="hidden"
                            disabled={isImageUploading}
                          />
                          {isImageUploading ? (
                            <>
                              <LogoLoader size="sm" />
                              <div className="text-xs text-muted">{t('components.ticket:newModal.images.uploading')}</div>
                            </>
                          ) : (
                            <>
                              <Upload className="h-5 w-5 text-light mb-1" />
                              <div className="text-xs text-muted">Add Photo</div>
                            </>
                          )}
                        </label>
                      )}
                    </div>
                  )}
                </div>
              </CornerLabelField>
            </div>

            {/* Right Column - Ticket image + preview + weather + chat + history */}
            <div className="ticket-modal-right-column">
              {/* First Ticket Image */}
              <div className="ticket-modal-section-card ticket-modal-preview-card ticket-modal-ticket-image-card">
                <div className="ticket-modal-preview-area">
                  {images[0] ? (
                    <img src={images[0]} alt="Ticket" className="w-full h-full object-cover" />
                  ) : (
                    <div className="ticket-modal-empty-preview">
                      <img src={iconLogoFolwOutline} alt="FOLW" className="ticket-modal-empty-preview-logo" />
                    </div>
                  )}

                  {editTicket && (
                    <div className="ticket-modal-image-action-rail">
                      <button
                        type="button"
                        onClick={handleTicketLike}
                        disabled={isLikeOperationInProgress || isReadOnlyEditTicket}
                        className="ticket-modal-image-action-button"
                        title={userHasLiked ? t('components.ticket:likes.unlike') : t('components.ticket:likes.like')}
                      >
                        {isLikeOperationInProgress ? (
                          <LogoLoader size="sm" />
                        ) : (
                          <img
                            src={userHasLiked ? iconHeartFilledSvg : iconHeartOutlineSvg}
                            alt={userHasLiked ? t('components.ticket:likes.unlike') : t('components.ticket:likes.like')}
                            className={`h-6 w-6 ${images[0] && !userHasLiked ? 'ticket-modal-image-action-icon-on-image' : ''}`}
                          />
                        )}
                      </button>

                      <span className={`ticket-modal-image-action-count ${images[0] ? 'ticket-modal-image-action-count-on-image' : 'ticket-modal-image-action-count-on-empty'}`}>
                        {isLikeOperationInProgress
                          ? '...'
                          : (localLikeCount !== undefined ? localLikeCount : (editTicket?.likes || 0))}
                      </span>

                      <button
                        type="button"
                        onClick={handleTicketBookmark}
                        disabled={isBookmarkOperationInProgress || !images[0] || isReadOnlyEditTicket}
                        className="ticket-modal-image-action-button"
                        title={isBookmarked ? t('components.ticket:bookmark.remove') : t('components.ticket:bookmark.add')}
                      >
                        {isBookmarkOperationInProgress ? (
                          <LogoLoader size="sm" />
                        ) : (
                          <img
                            src={isBookmarked ? iconBookmarkFilledSvg : iconBookmarkSvg}
                            alt={isBookmarked ? t('components.ticket:bookmark.remove') : t('components.ticket:bookmark.add')}
                            className={`h-6 w-6 ${images[0] ? 'ticket-modal-image-action-icon-on-image' : ''}`}
                          />
                        )}
                      </button>

                      <button
                        type="button"
                        onClick={handleExportPDF}
                        disabled={isExporting}
                        className="ticket-modal-image-action-button"
                        title={t('editModal.exportPdf') as string}
                      >
                        {isExporting ? (
                          <LogoLoader size="sm" />
                        ) : (
                          <img
                            src={webShareIconSvg}
                            alt={t('editModal.exportPdf') as string}
                            className={`h-6 w-6 ${images[0] ? 'ticket-modal-image-action-icon-on-image' : ''}`}
                          />
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Plan Preview */}
              {!hidePlanPreview && (
                <div className="ticket-modal-section-card ticket-modal-preview-card">
                  <div className="ticket-modal-preview-area">
                    {(editTicket?.planPreviewImage || editTicket?.planLocation || planLocation) ? (
                      <PlanLocationPreview
                        ticket={editTicket}
                        planLocation={planLocation}
                        projectId={projectId}
                        nextTicketNumber={nextTicketNumber}
                      />
                    ) : (
                      <div className="ticket-modal-empty-preview">
                        <img src={iconLogoFolwOutline} alt="FOLW" className="ticket-modal-empty-preview-logo" />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* WEATHER Section */}
              <CornerLabelField
                label={editTicket ? t('components.ticket:newModal.weather.title') + ' ' + t('components.ticket:newModal.weather.atCreation', { defaultValue: '(at creation)' }) : t('components.ticket:newModal.weather.title')}
                labelBgClassName="bg-[#d7d7d7]"
              >
                <div className="ticket-modal-section-card">
                  {!editTicket && (
                    <div className="flex justify-end mb-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => forceRefreshWeather(projectId)}
                        disabled={projectWeatherLoading}
                        className="h-6 w-6 p-0"
                        title={t('components.ticket:newModal.weather.refreshTitle')}
                      >
                        <RefreshCw className={`h-3 w-3 ${projectWeatherLoading ? 'animate-spin' : ''}`} />
                      </Button>
                    </div>
                  )}

                  {/* For existing tickets: show saved weather data (immutable) */}
                  {/* For new tickets: show current weather data */}
                  {editTicket && (editTicket.weatherTemperature || editTicket.weatherConditions || editTicket.weatherWind || editTicket.weatherPrecipitation || editTicket.weatherLocation || editTicket.weatherTimestamp) ? (
                    <div className="space-y-2 text-xs">
                      {editTicket.weatherTemperature && (
                        <div className="flex justify-between">
                          <span className="text-dark">{t('components.ticket:newModal.weather.temperature')}</span>
                          <span className="text-dark font-medium">{editTicket.weatherTemperature}</span>
                        </div>
                      )}
                      {editTicket.weatherWind && (
                        <div className="flex justify-between">
                          <span className="text-dark">{t('components.ticket:newModal.weather.wind')}</span>
                          <span className="text-dark font-medium">{editTicket.weatherWind}</span>
                        </div>
                      )}
                      {editTicket.weatherConditions && (
                        <div className="flex justify-between">
                          <span className="text-dark">{t('components.ticket:newModal.weather.conditions')}</span>
                          <span className="text-dark font-medium">{editTicket.weatherConditions}</span>
                        </div>
                      )}
                      {editTicket.weatherPrecipitation && (
                        <div className="flex justify-between">
                          <span className="text-dark">{t('components.ticket:newModal.weather.precipitation')}</span>
                          <span className="text-dark font-medium">{editTicket.weatherPrecipitation}</span>
                        </div>
                      )}
                      {editTicket.weatherLocation && (
                        <div className="flex justify-between">
                          <span className="text-dark">{t('components.ticket:newModal.weather.location')}</span>
                          <span className="text-dark font-medium">{editTicket.weatherLocation}</span>
                        </div>
                      )}
                      {editTicket.weatherTimestamp && (
                        <div className="flex justify-between text-muted text-[10px] mt-2 pt-2 border-t border-light">
                          <span>{t('components.ticket:newModal.weather.capturedAt', { defaultValue: 'Captured' })}</span>
                          <span>{formatDateTime(editTicket.weatherTimestamp)}</span>
                        </div>
                      )}
                    </div>
                  ) : !editTicket && weatherData ? (
                    <div className="space-y-2 text-xs">
                      <div className="flex justify-between">
                        <span className="text-dark">{t('components.ticket:newModal.weather.temperature')}</span>
                        <span className="text-dark font-medium">
                          {weatherData.temperature}°C
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-dark">{t('components.ticket:newModal.weather.wind')}</span>
                        <span className="text-dark font-medium">
                          {weatherData.windSpeed} km/h
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-dark">{t('components.ticket:newModal.weather.conditions')}</span>
                        <span className="text-dark font-medium">
                          {weatherData.condition}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-dark">{t('components.ticket:newModal.weather.humidity')}</span>
                        <span className="text-dark font-medium">
                          {weatherData.humidity || 'N/A'}%
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-muted">
                      {projectWeatherLoading ? t('components.ticket:newModal.weather.loading') : t('components.ticket:newModal.weather.notAvailable')}
                    </div>
                  )}
                </div>
              </CornerLabelField>

              {/* NACHRICHT AN TEAM-MITGLIED Section */}
              {editTicket && (
                <CornerLabelField
                  label={tChat('components.ticketChat:inlineTitle')}
                  labelBgClassName="bg-[#d7d7d7]"
                >
                  <div className="ticket-modal-section-card ticket-modal-chat-card">
                    <div
                      ref={chatScrollContainerRef}
                      className="ticket-modal-chat-list"
                    >
                      {isChatLoading ? (
                        <div className="p-4 text-center text-xs text-muted">
                          {tChat('components.ticketChat:loading')}
                        </div>
                      ) : (
                        <div className="p-3 space-y-2">
                          {messages.length === 0 ? (
                            <div className="text-xs text-muted italic text-center py-4">
                              {tChat('components.ticketChat:empty.line1')}
                            </div>
                          ) : (
                            messages.map((message) => {
                              const isSystemMessage = message.userId === SYSTEM_USER_ID;
                              const isCurrentUser = userId !== null && message.userId === userId;

                              // System messages: centered, no bubble, italic, muted
                              if (isSystemMessage) {
                                return (
                                  <div key={message.id} className="flex justify-center py-2">
                                    <div className="text-xs text-gray-500 italic text-center max-w-[80%]">
                                      {message.message}
                                    </div>
                                  </div>
                                );
                              }

                              // Regular user messages with bubble
                              return (
                                <div
                                  key={message.id}
                                  className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'} mb-2`}
                                >
                                  <div
                                    className={`ticket-modal-chat-bubble ${isCurrentUser ? 'ticket-modal-chat-bubble-self' : 'ticket-modal-chat-bubble-other'}`}
                                  >
                                    <div className={`flex items-center gap-2 mb-1 ${
                                      isCurrentUser ? 'text-white' : 'text-dark'
                                    }`}>
                                      <span className="font-semibold text-xs">
                                        {message.userName || message.userCode || 'Unknown'}
                                      </span>
                                      <span className={`text-xs ${
                                        isCurrentUser ? 'text-white/80' : 'text-muted'
                                      }`}>
                                        {formatTimeShort(message.createdAt)}
                                      </span>
                                    </div>
                                    <div className={`text-xs ${
                                      isCurrentUser ? 'text-white' : 'text-dark'
                                    }`}>
                                      {message.message}
                                    </div>
                                  </div>
                                </div>
                              );
                            })
                          )}
                          <div ref={chatEndRef} />
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder={tChat('components.ticketChat:inputPlaceholder')}
                        className="ticket-modal-chat-input flex-1 h-8 px-4 text-xs bg-white border border-black rounded-full shadow-none focus:outline-none focus:border-black disabled:opacity-50"
                        disabled={sendMessageMutation.isPending || !canMessageInCurrentContext}
                      />
                      <button
                        onClick={handleSendMessage}
                        disabled={!newMessage.trim() || sendMessageMutation.isPending || !canMessageInCurrentContext}
                        className="ticket-modal-chat-send h-8 w-8 p-0 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      >
                        {sendMessageMutation.isPending ? "..." : (
                          <img src={webIconSendSvg} alt="Send" className="h-8 w-8" />
                        )}
                      </button>
                    </div>
                  </div>
                </CornerLabelField>
              )}

              {/* HISTORY Section */}
              {editTicket && (
                <CornerLabelField
                  label="HISTORY"
                  labelBgClassName="bg-[#d7d7d7]"
                >
                  <div className="ticket-modal-section-card">
                    <TicketHistory ticketId={editTicket.id} />
                  </div>
                </CornerLabelField>
              )}

              {/* TICKET CREATOR Section */}
              {editTicket && (
                <CornerLabelField
                  label={t('components.ticket:newModal.creator.title')}
                  labelBgClassName="bg-[#d7d7d7]"
                >
                  <div className="ticket-modal-section-card ticket-modal-creator-card">
                    <div className="ticket-modal-creator-content">
                      <div className="ticket-modal-creator-text">
                        <p className="ticket-modal-creator-name">{creatorName}</p>
                        {creatorBio ? (
                          <p className="ticket-modal-creator-bio">{creatorBio}</p>
                        ) : (
                          <p className="ticket-modal-creator-bio ticket-modal-creator-bio-muted">
                            {t('components.ticket:newModal.creator.bioFallback')}
                          </p>
                        )}
                      </div>
                      <div className="ticket-modal-creator-avatar-wrap">
                        {creatorProfilePhotoUrl ? (
                          <img
                            src={creatorProfilePhotoUrl}
                            alt={creatorName}
                            className="ticket-modal-creator-avatar"
                          />
                        ) : (
                          <div className="ticket-modal-creator-avatar ticket-modal-creator-avatar-fallback">
                            <img src={iconLogoFolwOutline} alt="FOLW" className="ticket-modal-empty-preview-logo" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CornerLabelField>
              )}

            </div>
          </div>

        </form>
      </Form>

      {/* Image Editor Modal */}
      {showImageEditor && editingImageIndex !== null && images[editingImageIndex] && (
        <ImageEditor
          isOpen={showImageEditor}
          imageSrc={images[editingImageIndex]}
          onClose={handleImageEditorClose}
          onSave={handleImageEditorSave}
        />
      )}

      {/* Image Preview Modal */}
      <Dialog open={previewImageIndex !== null} onOpenChange={(open) => !open && setPreviewImageIndex(null)}>
        <DialogContent className="ticket-preview-modal-shell [&>button]:hidden">
          <DialogTitle className="sr-only">
            {previewImageIndex !== null && images[previewImageIndex]
              ? `Image ${previewImageIndex + 1} of ${images.length}`
              : 'Image Preview'}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {previewImageIndex !== null && images[previewImageIndex]
              ? `Previewing image ${previewImageIndex + 1} of ${images.length}. Use arrow keys or navigation buttons to browse through images.`
              : 'Image preview dialog'}
          </DialogDescription>
          <button
            type="button"
            className="ticket-preview-modal-close"
            onClick={() => setPreviewImageIndex(null)}
            aria-label="Close image preview"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="ticket-preview-modal-content">
            {previewImageIndex !== null && images[previewImageIndex] && (() => {
              const currentImageUrl = images[previewImageIndex];
              
              return (
                <div className="ticket-preview-modal-layout">
                  <div className="ticket-preview-image-shell">
                    <div
                      className="ticket-preview-image-frame"
                      onPointerDown={handlePreviewPointerDown}
                      onPointerUp={handlePreviewPointerUp}
                    >
                      <img
                        src={currentImageUrl}
                        alt={`Image ${previewImageIndex + 1} of ${images.length}`}
                        className="ticket-preview-image"
                      />

                      <div className="ticket-preview-ticket-badge">
                        #{editTicket ? editTicket.ticketNumber : nextTicketNumber}
                      </div>

                      {images.length > 1 && (
                        <>
                          <button
                            type="button"
                            className="ticket-preview-nav ticket-preview-nav-left"
                            onClick={(e) => {
                              e.stopPropagation();
                              goToPreviousPreviewImage();
                            }}
                            aria-label="Previous image"
                          >
                            <ChevronLeft className="h-5 w-5" />
                          </button>
                          <button
                            type="button"
                            className="ticket-preview-nav ticket-preview-nav-right"
                            onClick={(e) => {
                              e.stopPropagation();
                              goToNextPreviewImage();
                            }}
                            aria-label="Next image"
                          >
                            <ChevronRight className="h-5 w-5" />
                          </button>
                        </>
                      )}

                      {(editTicket || canUseAI) && (
                        <div className="ticket-preview-action-rail">
                          {canUseAI && (
                            <button
                              type="button"
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (!currentImageUrl || isAnalyzing) return;
                                await runAIAnalysisForImage(currentImageUrl);
                              }}
                              disabled={isAnalyzing}
                              className="ticket-preview-action-button"
                              title={t('components.ticket:newModal.images.runAIAnalysis') as string}
                            >
                              {isAnalyzing ? (
                                <LogoLoader size="sm" />
                              ) : (
                                <Sparkles className="h-7 w-7 ticket-modal-image-action-icon-on-image" />
                              )}
                            </button>
                          )}
                          {editTicket && (
                            <>
                          <button
                            type="button"
                            onClick={handleTicketLike}
                            disabled={isLikeOperationInProgress || isReadOnlyEditTicket}
                            className="ticket-preview-action-button"
                            title={userHasLiked ? t('components.ticket:likes.unlike') : t('components.ticket:likes.like')}
                          >
                            {isLikeOperationInProgress ? (
                              <LogoLoader size="sm" />
                            ) : (
                              <img
                                src={userHasLiked ? iconHeartFilledSvg : iconHeartOutlineSvg}
                                alt={userHasLiked ? t('components.ticket:likes.unlike') : t('components.ticket:likes.like')}
                                className={`h-7 w-7 ${!userHasLiked ? 'ticket-modal-image-action-icon-on-image' : ''}`}
                              />
                            )}
                          </button>
                          <span className="ticket-preview-action-count ticket-modal-image-action-count-on-image">
                            {isLikeOperationInProgress
                              ? '...'
                              : (localLikeCount !== undefined ? localLikeCount : (editTicket?.likes || 0))}
                          </span>
                          <button
                            type="button"
                            onClick={handleTicketBookmark}
                            disabled={isBookmarkOperationInProgress || !images[0] || isReadOnlyEditTicket}
                            className="ticket-preview-action-button"
                            title={isBookmarked ? t('components.ticket:bookmark.remove') : t('components.ticket:bookmark.add')}
                          >
                            {isBookmarkOperationInProgress ? (
                              <LogoLoader size="sm" />
                            ) : (
                              <img
                                src={isBookmarked ? iconBookmarkFilledSvg : iconBookmarkSvg}
                                alt={isBookmarked ? t('components.ticket:bookmark.remove') : t('components.ticket:bookmark.add')}
                                className="h-7 w-7 ticket-modal-image-action-icon-on-image"
                              />
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={handleExportPDF}
                            disabled={isExporting}
                            className="ticket-preview-action-button"
                            title={t('editModal.exportPdf') as string}
                          >
                            {isExporting ? (
                              <LogoLoader size="sm" />
                            ) : (
                              <img
                                src={webShareIconSvg}
                                alt={t('editModal.exportPdf') as string}
                                className="h-7 w-7 ticket-modal-image-action-icon-on-image"
                              />
                            )}
                          </button>
                            </>
                          )}
                        </div>
                      )}

                      <div className="ticket-preview-bottom-overlay">
                        <div className="ticket-preview-creator">
                          {previewAddedByProfilePhotoUrl ? (
                            <img
                              src={previewAddedByProfilePhotoUrl}
                              alt={previewAddedByDisplayName}
                              className="ticket-preview-creator-avatar"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="ticket-preview-creator-avatar ticket-preview-creator-avatar-fallback">
                              {getInitials(previewAddedByDisplayName)}
                            </div>
                          )}
                          <div className="ticket-preview-creator-texts">
                            <p className="ticket-preview-creator-name">{previewAddedByDisplayName}</p>
                            <p className="ticket-preview-location">{previewLocation}</p>
                          </div>
                        </div>
                        <div className="ticket-preview-counter">
                          {previewImageIndex + 1} / {images.length}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="ticket-preview-bottom-stack">
                    <div className="ticket-preview-description-shell">
                      <CornerLabelField
                        label={t('components.ticket:fields.description')}
                        labelBgClassName="bg-[#d4d4d4]"
                        className="h-full"
                      >
                        <div className="ticket-preview-description-box">
                          {previewDescriptionLines.length > 0 ? (
                            <div className="ticket-preview-description-content">
                              {previewDescriptionLines.map((line, idx) => (
                                <p key={`${idx}-${line.slice(0, 20)}`} className="ticket-preview-description-line">
                                  {line}
                                </p>
                              ))}
                            </div>
                          ) : (
                            <div className="ticket-preview-description-empty" />
                          )}
                        </div>
                      </CornerLabelField>
                    </div>

                    {previewOrganizationName ? (
                      <div className="ticket-preview-organization-footer">
                        {previewOrganizationName}
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>

      {/* Export Options Modal */}
      <TicketExportOptionsModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onExport={performExport}
      />

      {/* AI Image Analysis Dialog */}
      {showAnalysisDialog && pendingAnalysis && (
        <ImageAnalysisDialog
          isOpen={showAnalysisDialog}
          onClose={() => {
            setShowAnalysisDialog(false);
            setPendingAnalysis(null);
          }}
          analysis={pendingAnalysis.analysis}
          imageUrl={pendingAnalysis.imageUrl}
          ticketId={editTicket?.id || ''}
          onApprove={async (userDescription, useForTicketDescription, saveAsImageDescription) => {
            // Update the analysis in allAnalyses array
            setAllAnalyses(prev => prev.map(a => 
              a.imageUrl === pendingAnalysis.imageUrl 
                ? {
                    ...a,
                    approved: true,
                    useForTicket: useForTicketDescription || false,
                    saveAsImageDesc: saveAsImageDescription || false,
                    editedDescription: userDescription || a.editedDescription,
                  }
                : a
            ));

            // Update local description immediately if user wants to save as image description
            if (saveAsImageDescription && userDescription) {
              setLocalImageDescriptions(prev => {
                const newMap = new Map(prev);
                newMap.set(pendingAnalysis.imageUrl, userDescription);
                return newMap;
              });
            }

            // Apply to ticket description immediately when requested
            if (useForTicketDescription && userDescription) {
              form.setValue('description', userDescription, {
                shouldDirty: true,
                // Avoid triggering full-form validation while AI fills description.
                // Validation runs on explicit save via submitWithValidation.
                shouldValidate: false,
                shouldTouch: true,
              });
            }

            // If this is an existing ticket, save to DB immediately
            if (editTicket?.id && pendingAnalysis?.analysis?.id && !pendingAnalysis.analysis.isPreview) {
              try {
                await apiClient.saveImageAnalysisApproval(
                  pendingAnalysis.analysis.id,
                  true,
                  userDescription,
                  useForTicketDescription,
                  saveAsImageDescription,
                  pendingAnalysis.imageUrl // Pass imageUrl for better matching
                );
                
                // Invalidate image metadata query to refresh the description (but local state already updated)
                queryClient.invalidateQueries({ queryKey: [`/api/tickets/${editTicket.id}/image-metadata`] });
                
                toast({
                  title: t('components.ticket:editModal.toasts.aiApproved') || 'Analysis approved',
                  description: t('components.ticket:editModal.toasts.aiApprovedDesc') || 'The AI analysis was approved and added to the ticket.',
                });
              } catch (error) {
                console.error('Failed to save analysis approval:', error);
                // Even if save fails, keep the local description for immediate feedback
              }
            } else if (!editTicket?.id) {
              // For new tickets, just update local state (will be saved after ticket creation)
              // Local state already updated above
            }

            setShowAnalysisDialog(false);
            setPendingAnalysis(null);
          }}
          onReject={() => {
            // Mark as rejected in allAnalyses
            setAllAnalyses(prev => prev.map(a => 
              a.imageUrl === pendingAnalysis.imageUrl 
                ? { ...a, approved: false }
                : a
            ));
            setShowAnalysisDialog(false);
            setPendingAnalysis(null);
          }}
          onUseForTicketDescription={async (description: string) => {
            // Update ticket description field
            form.setValue('description', description);
          }}
          onSaveAsImageDescription={async (description: string) => {
            // Save to image metadata (would need API endpoint)
            // For now, just log it
            console.log('Saving image description:', description);
          }}
        />
      )}

      {/* Analysis Review Dialog - Shows before ticket save for new tickets */}
      {showReviewDialog && (
        <AnalysisReviewDialog
          isOpen={showReviewDialog}
          onClose={() => {
            setShowReviewDialog(false);
            pendingSubmissionRef.current = null;
          }}
          analyses={allAnalyses}
          onSaveSelected={async (selectedAnalyses) => {
            // Apply selected analyses to form
            const analysesForTicket = selectedAnalyses.filter(a => a.useForTicket);
            if (analysesForTicket.length > 0) {
              // Merge all selected descriptions
              const mergedDescription = analysesForTicket
                .map(a => a.editedDescription || a.analysis?.analysisText || '')
                .filter(Boolean)
                .join('\n\n');
              
              // Update form description field
              const currentDescription = form.getValues('description') || '';
              const newDescription = currentDescription 
                ? `${currentDescription}\n\n${mergedDescription}`
                : mergedDescription;
              form.setValue('description', newDescription);
            }

            // Update local descriptions for images that should be saved as image descriptions
            selectedAnalyses.forEach(analysis => {
              if (analysis.saveAsImageDesc && analysis.editedDescription) {
                setLocalImageDescriptions(prev => {
                  const newMap = new Map(prev);
                  newMap.set(analysis.imageUrl, analysis.editedDescription || analysis.analysis?.analysisText || '');
                  return newMap;
                });
              }
            });

            // Store analyses for saving after ticket creation
            setAllAnalyses(selectedAnalyses);

            // Now submit the form
            if (pendingSubmissionRef.current) {
              await latestOnSubmitRef.current(pendingSubmissionRef.current);
              pendingSubmissionRef.current = null;
            }
          }}
          onIgnoreAll={() => {
            // Clear analyses and submit form
            setAllAnalyses([]);
            if (pendingSubmissionRef.current) {
              latestOnSubmitRef.current(pendingSubmissionRef.current);
              pendingSubmissionRef.current = null;
            }
          }}
        />
      )}
    </div>
  );
}

// Plan Location Preview Component - Uses stored preview image or generates on-the-fly
export function PlanLocationPreview({ 
  ticket, 
  planLocation, 
  projectId,
  nextTicketNumber
}: { 
  ticket?: Ticket | null; 
  planLocation?: {x: number, y: number, planId: string, zoom: number}; 
  projectId: string;
  nextTicketNumber?: string;
}) {
  const { t } = useTranslation(['components.ticket']);
  const [generatedPreview, setGeneratedPreview] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  
  const currentPlanLocation = ticket?.planLocation || planLocation;
  const planPreviewImage = ticket?.planPreviewImage || generatedPreview;
  
  // Get plan data for generating preview on-the-fly
  const { data: plansResponse } = useQuery({
    queryKey: QUERY_KEYS.projectPlans(projectId),
    queryFn: async () => {
      const response = await apiRequest(`/api/plans/project/${projectId}?limit=200&offset=0`);
      return await response.json();
    },
    enabled: !!projectId && !!currentPlanLocation,
  });

  const plans = plansResponse?.data || [];
  const currentPlan = currentPlanLocation
    ? plans.find((p: Plan) => p.id === currentPlanLocation.planId)
    : null;
  const planDisplayName =
    currentPlan?.name ||
    (currentPlan as any)?.fileName ||
    (ticket as any)?.planName ||
    '';
  
  // Get status data for pin color
  const { organizationId } = useCurrentOrganization();
  const { data: statusResponse } = useQuery({
    queryKey: ["/api/status", organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/status?organizationId=${encodeURIComponent(organizationId)}` : "/api/status";
      const response = await apiRequest(url);
      return await response.json();
    },
  });
  const statusData = statusResponse?.data || [];
  
  // Generate preview on-the-fly if planLocation exists but no stored image
  useEffect(() => {
    if (currentPlanLocation && !ticket?.planPreviewImage && !generatedPreview && !isGenerating) {
      const plan = plans.find((p: Plan) => p.id === currentPlanLocation.planId);
      if (plan?.fileUrl) {
        setIsGenerating(true);
        const ticketNum = ticket?.ticketNumber || nextTicketNumber || 'N';
        
        // Get status color for the pin
        const statusItem = (statusData as any[]).find((s: any) => s.id === (ticket as any)?.statusId);
        const pinColor = statusItem?.color || '#ef4444'; // Default to red for open status
        
        PlanImageCaptureService.capturePlanSection(
          plan.fileUrl,
          currentPlanLocation,
          2, // zoom level (2x for better detail)
          { width: 400, height: 300 }, // capture size
          ticketNum, // render pin with ticket number in image
          pinColor // pin color based on status
        )
          .then((result) => {
            setGeneratedPreview(result.imageData);
          })
          .catch((error) => {
            console.error('Error generating plan preview:', error);
          })
          .finally(() => {
            setIsGenerating(false);
          });
      }
    }
  }, [currentPlanLocation, ticket?.planPreviewImage, generatedPreview, isGenerating, plans, ticket?.ticketNumber, nextTicketNumber, (ticket as any)?.statusId, statusData, organizationId]);
  
  if (!currentPlanLocation) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-xs text-slate-400 text-center">
          {t('components.ticket:plan.noPlan')}
        </div>
      </div>
    );
  }
  
  // Display preview image (stored or generated)
  if (planPreviewImage) {
    return (
      <div className="w-full h-full flex flex-col bg-white">
        <div className="relative flex-1 overflow-hidden min-h-0">
          {/* Display preview image (pin is already rendered in the image) */}
          <img
            src={planPreviewImage}
            alt={`Plan preview for ticket ${ticket?.ticketNumber || nextTicketNumber || 'N'}`}
            className="absolute inset-0 h-full w-full object-cover"
          />
          {/* Pin is now rendered directly in the image, no overlay needed */}
        </div>
        {planDisplayName ? (
          <div className="ticket-modal-plan-name" title={planDisplayName}>
            <span className="ticket-modal-plan-name-label">{t('components.ticket:plan.nameLabel')}</span>
            <span className="ticket-modal-plan-name-value">{planDisplayName}</span>
          </div>
        ) : null}
      </div>
    );
  }
  
  // Loading state while generating preview
  if (isGenerating) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-xs text-slate-400 text-center">
          {t('components.ticket:plan.loading') || 'Loading preview...'}
        </div>
      </div>
    );
  }
  
  // Fallback: No preview available
  return (
    <div className="h-full flex items-center justify-center">
      <div className="text-xs text-slate-400 text-center">
        {t('components.ticket:plan.noPlan')}
      </div>
    </div>
  );
}
